import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aiGovApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AiGovScheduleDialog from '../components/AiGovScheduleDialog';

const P  = '#6B3FA0';
const PD = '#4A2B70';
const PL = '#F3EEFA';

const STATUS_COLORS = {
  active:    { bg: '#D1FAE5', text: '#065F46' },
  paused:    { bg: '#FEF3C7', text: '#92400E' },
  cancelled: { bg: '#FEE2E2', text: '#991B1B' },
};
const TASK_STATUS_COLORS = {
  pending:   { bg: '#F3F4F6', text: '#374151' },
  due:       { bg: '#FEF3C7', text: '#92400E' },
  overdue:   { bg: '#FEE2E2', text: '#991B1B' },
  completed: { bg: '#D1FAE5', text: '#065F46' },
  skipped:   { bg: '#E5E7EB', text: '#6B7280' },
};
const DOC_STATUS_COLORS = {
  draft:    { bg: '#EFF6FF', text: '#1E40AF' },
  sent:     { bg: '#FEF3C7', text: '#92400E' },
  signed:   { bg: '#D1FAE5', text: '#065F46' },
  archived: { bg: '#E5E7EB', text: '#6B7280' },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const getRootDomain = (domain) => {
  if (!domain || domain === '—') return domain;
  const parts = domain.split('.');
  if (parts.length <= 2) return domain;
  const lastTwo = parts.slice(-2).join('.');
  const threePartTlds = ['co.uk', 'com.au', 'co.nz', 'org.uk', 'net.au', 'co.jp', 'com.br'];
  if (threePartTlds.includes(lastTwo) && parts.length > 2) return parts.slice(-3).join('.');
  return lastTwo;
};

// Groups a flat array of domain rows into root-domain buckets.
// countFn(row) should return the numeric count for that row.
const groupByRootDomain = (rows, domainFn, countFn) => {
  const map = {};
  rows.forEach(row => {
    const domain = domainFn(row);
    const root = getRootDomain(domain);
    if (!map[root]) map[root] = { root, total: 0, totalBlocked: 0, rows: [] };
    map[root].total += countFn(row) || 0;
    map[root].totalBlocked += (row.requestsBlocked ?? row.blocked ?? 0);
    map[root].rows.push(row);
  });
  return Object.values(map)
    .sort((a, b) => b.total - a.total)
    .map(g => ({ ...g, rows: g.rows.sort((a, b) => (countFn(b) || 0) - (countFn(a) || 0)) }));
};

const TABS = ['Overview', 'Documents', 'Tasks', 'Activity'];

export default function AiGovernanceProgram() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [program, setProgram]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('Overview');

  // Documents state
  const [templates, setTemplates]   = useState([]);
  const [genTemplateId, setGenTemplateId] = useState('');
  const [generating, setGenerating] = useState(false);

  // Tasks state
  const [completingTask, setCompletingTask] = useState(null);
  const [taskNotes, setTaskNotes]   = useState('');
  const [showTaskHistory, setShowTaskHistory] = useState(false);
  const [schedulingTask, setSchedulingTask]   = useState(null);

  // Umbrella state
  const [umbrellaLoading, setUmbrellaLoading] = useState(false);
  const [umbrellaCategories, setUmbrellaCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [umbrellaDatePreset, setUmbrellaDatePreset] = useState('30d');
  const [umbrellaCustomFrom, setUmbrellaCustomFrom] = useState('');
  const [umbrellaCustomTo, setUmbrellaCustomTo] = useState('');
  const [generatingReport, setGeneratingReport]           = useState(false);
  const [generatingOverviewReport, setGeneratingOverviewReport] = useState(false);

  const getUmbrellaDateParams = (preset, customFrom, customTo) => {
    const p = preset || umbrellaDatePreset;
    const cf = customFrom !== undefined ? customFrom : umbrellaCustomFrom;
    const ct = customTo !== undefined ? customTo : umbrellaCustomTo;
    const now = new Date();
    const ms = d => String(d.getTime()); // Umbrella requires Unix ms for fixed dates
    switch (p) {
      case '7d':        return { from: '-7days', to: 'now' };
      case '30d':       return { from: '-30days', to: 'now' };
      case 'thisMonth': return { from: ms(new Date(now.getFullYear(), now.getMonth(), 1)), to: 'now' };
      case 'lastMonth': {
        const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const last  = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return { from: ms(first), to: ms(last) };
      }
      case 'thisYear':   return { from: ms(new Date(now.getFullYear(), 0, 1)), to: 'now' };
      case 'trailing12': return { from: '-365days', to: 'now' };
      case 'custom':     return {
        from: cf ? ms(new Date(cf)) : '-30days',
        to:   ct ? ms(new Date(ct + 'T23:59:59')) : 'now',
      };
      default:           return { from: '-30days', to: 'now' };
    }
  };
  const [umbrellaSubTab, setUmbrellaSubTab] = useState('categories');
  const [umbrellaOrgCategories, setUmbrellaOrgCategories] = useState(null);   // this org only
  const [umbrellaAllOrgs, setUmbrellaAllOrgs] = useState(null);               // full MSP view (for matching)
  const [umbrellaAiApps, setUmbrellaAiApps] = useState(null);
  const [umbrellaAppsSource, setUmbrellaAppsSource] = useState('msp-wide');
  const [umbrellaIdentities, setUmbrellaIdentities] = useState(null);
  const [identitiesLoading, setIdentitiesLoading] = useState(false);
  const [dnsExpandedIdentity, setDnsExpandedIdentity] = useState(null);
  const [umbrellaUserActivity, setUmbrellaUserActivity] = useState(null);
  const [userActivityLoading, setUserActivityLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedAppDomain, setExpandedAppDomain] = useState(null);
  const [expandedAppTool, setExpandedAppTool]     = useState(null);
  const [expandedUserDomain, setExpandedUserDomain] = useState({});
  const [completingFeature, setCompletingFeature] = useState(null);
  const [completeNotes, setCompleteNotes] = useState('');
  const [umbrellaAvailableOrgs, setUmbrellaAvailableOrgs] = useState([]);     // unique orgs from MSP data
  const [umbrellaMatchedOrgId, setUmbrellaMatchedOrgId] = useState(null);     // resolved org ID
  const [orgPickerValue, setOrgPickerValue] = useState('');
  const [savingOrgMatch, setSavingOrgMatch] = useState(false);

  // Edit program modal
  const [showEdit, setShowEdit]     = useState(false);
  const [tiers, setTiers]           = useState([]);
  const [editForm, setEditForm]     = useState({});
  const [saving, setSaving]         = useState(false);

  // Merge fields for document generation
  const [mergeFields, setMergeFields] = useState({});
  const [mergeFieldsEditing, setMergeFieldsEditing] = useState(false);
  const [savingMergeFields, setSavingMergeFields] = useState(false);

  // Approved tools
  const [approvedTools, setApprovedTools] = useState([]);
  const [toolRegistry, setToolRegistry] = useState([]);
  const [toolModalOpen, setToolModalOpen] = useState(false);
  const [toolForm, setToolForm] = useState({});
  const [editingToolId, setEditingToolId] = useState(null);
  const [savingTool, setSavingTool] = useState(false);
  const [registrySearch, setRegistrySearch] = useState('');
  const [toolCustomMode, setToolCustomMode] = useState(false);

  // Trigger assessment
  const [triggering, setTriggering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [progRes, tmplRes, tiersRes, toolsRes, registryRes] = await Promise.all([
        aiGovApi.getProgram(programId),
        aiGovApi.listTemplates(),
        aiGovApi.getTiers(),
        aiGovApi.listApprovedTools(programId),
        aiGovApi.getToolRegistry(),
      ]);
      setProgram(progRes.data);
      setTemplates(tmplRes.data.templates || []);
      setTiers(tiersRes.data.tiers || []);
      setApprovedTools(toolsRes.data.tools || []);
      setToolRegistry(registryRes.data.tools || []);
      const mf = progRes.data.merge_fields;
      const savedMf = typeof mf === 'string' ? JSON.parse(mf || '{}') : (mf || {});
      const prog = progRes.data;
      const todayStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const fmtD = d => {
        if (!d) return '';
        const datePart = String(d).split('T')[0].split(' ')[0];
        const parsed = new Date(datePart + 'T00:00:00');
        return isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      };
      const prefill = {
        client_name:      prog.client_name || '',
        ir_contact_name:  prog.contact_name || '',
        contact_name:     prog.contact_name || '',
        ir_contact_email: prog.contact_email || '',
        contact_email:    prog.contact_email || '',
        start_date:       fmtD(prog.start_date),
        today:            todayStr,
        tier:             prog.tier_name || '',
        document_version: '1.0',
      };
      // saved values override prefill
      setMergeFields({ ...prefill, ...savedMf });
      setEditForm({
        tier_id: progRes.data.tier_id,
        status: progRes.data.status,
        next_assessment_type: progRes.data.next_assessment_type,
        umbrella_org_id: progRes.data.umbrella_org_id || '',
        umbrella_org_name: progRes.data.umbrella_org_name || '',
        notes: progRes.data.notes || '',
      });
    } catch {
      toast.error('Failed to load program');
      navigate('/ai-gov');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [programId]);

  useEffect(() => {
    if (activeTab === 'Activity' && !categoriesLoaded) {
      loadUmbrellaCategories().then(ids => {
        if (program?.umbrella_org_id) loadUmbrellaOrgData(ids);
      });
    } else if (activeTab === 'Activity' && categoriesLoaded && umbrellaOrgCategories === null && program?.umbrella_org_id) {
      loadUmbrellaOrgData(selectedCategories);
    }
  }, [activeTab, program]);

  const handleStartAssessment = async (type) => {
    setTriggering(true);
    try {
      const res = await aiGovApi.startAssessment(programId, type);
      toast.success('Assessment created — redirecting…');
      navigate(`/assessments/${res.data.assessment_id}/take`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create assessment');
    } finally {
      setTriggering(false);
    }
  };

  const handleGenerateDoc = async () => {
    if (!genTemplateId) { toast.error('Select a template'); return; }
    setGenerating(true);
    try {
      await aiGovApi.generateDocument(programId, parseInt(genTemplateId));
      toast.success('Document generated');
      setGenTemplateId('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadDoc = async (docId) => {
    try {
      await aiGovApi.downloadDocument(docId);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleDocStatus = async (docId, status) => {
    try {
      await aiGovApi.updateDocumentStatus(docId, status);
      toast.success('Status updated');
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteDoc = async (docId, docName) => {
    if (!window.confirm(`Delete "${docName}"? This cannot be undone.`)) return;
    try {
      await aiGovApi.deleteDocument(docId);
      toast.success('Document deleted');
      load();
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await aiGovApi.completeTask(programId, taskId, taskNotes);
      toast.success('Task completed');
      setCompletingTask(null);
      setTaskNotes('');
      load();
    } catch {
      toast.error('Failed to complete task');
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await aiGovApi.updateProgram(programId, {
        ...editForm,
        tier_id: parseInt(editForm.tier_id),
      });
      toast.success('Program updated');
      setShowEdit(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const decodeHtml = (str) => {
    try {
      const el = document.createElement('textarea');
      el.innerHTML = str || '';
      return el.value;
    } catch { return str || ''; }
  };

  const loadUmbrellaCategories = async () => {
    if (categoriesLoaded) return;
    try {
      const res = await aiGovApi.getUmbrellaCategories();
      const cats = res.data.categories || [];
      setUmbrellaCategories(cats);
      const aiKeywords = ['generative', 'artificial intelligence', 'conversational', 'code assist',
        'image generat', 'machine learning', 'chatbot', 'llm', 'openai', 'chatgpt', 'data analysis'];
      const aiCats = cats.filter(c =>
        aiKeywords.some(kw => (c.label || c.name || '').toLowerCase().includes(kw))
      );
      const ids = aiCats.length > 0 ? aiCats.map(c => c.id) : [];
      setSelectedCategories(ids);
      setCategoriesLoaded(true);
      return ids;
    } catch {
      setCategoriesLoaded(true);
      return [];
    }
  };

  const loadUmbrellaOrgData = async (categoryIds, preset, customFrom, customTo) => {
    const { from, to } = getUmbrellaDateParams(preset, customFrom, customTo);
    setUmbrellaLoading(true);
    const catParam = categoryIds && categoryIds.length > 0 ? categoryIds.join(',') : undefined;
    const byOrgParams = { from, to, limit: 100, ...(catParam ? { categories: catParam } : {}) };

    const [byOrgResult] = await Promise.allSettled([
      aiGovApi.getUmbrellaCategoryByOrg(byOrgParams),
    ]);

    if (byOrgResult.status === 'fulfilled') {
      const allRows = byOrgResult.value.data.by_org || [];
      setUmbrellaAllOrgs(allRows);

      // Build unique org list for the picker
      const orgMap = {};
      allRows.forEach(r => {
        if (r.organizationId && !orgMap[r.organizationId])
          orgMap[r.organizationId] = decodeHtml(r.organizationName);
      });
      const available = Object.entries(orgMap)
        .map(([id, name]) => ({ id: Number(id), name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setUmbrellaAvailableOrgs(available);

      // Resolve which org to show: stored ID → exact name match → fuzzy → null (show picker)
      const storedId = program?.umbrella_org_id ? Number(program.umbrella_org_id) : null;
      const clientName = (program?.client_name || '').toLowerCase().trim();
      let resolvedId = storedId;
      let autoSave = false;

      if (!resolvedId) {
        const exact = available.find(o => o.name.toLowerCase() === clientName);
        if (exact) { resolvedId = exact.id; autoSave = true; }
      }
      if (!resolvedId) {
        const fuzzy = available.find(o =>
          o.name.toLowerCase().includes(clientName) || clientName.includes(o.name.toLowerCase())
        );
        if (fuzzy) resolvedId = fuzzy.id; // fuzzy — don't auto-save, let user confirm
      }

      setUmbrellaMatchedOrgId(resolvedId);
      const orgRows = resolvedId ? allRows.filter(r => r.organizationId === resolvedId) : [];
      setUmbrellaOrgCategories(orgRows);
      setUmbrellaSubTab(orgRows.length > 0 ? 'categories' : 'apps');

      // Silently persist exact name match back to program record
      if (autoSave && !storedId) {
        const matchedOrg = available.find(o => o.id === resolvedId);
        try {
          await aiGovApi.updateProgram(programId, {
            ...editForm, tier_id: parseInt(editForm.tier_id),
            umbrella_org_id: String(resolvedId),
            umbrella_org_name: matchedOrg?.name || '',
          });
        } catch { /* non-fatal */ }
      }

      // Per-org top AI destinations via child org token
      const resolvedOrgId = resolvedId || program?.umbrella_org_id;
      const appsParams = { from, to, limit: 100, ...(catParam ? { categories: catParam } : {}), ...(resolvedOrgId ? { orgId: resolvedOrgId } : {}) };
      try {
        const appsRes = await aiGovApi.getUmbrellaAiApps(appsParams);
        setUmbrellaAiApps(appsRes.data.apps || []);
        setUmbrellaAppsSource(appsRes.data.source || 'msp-wide');
      } catch {
        setUmbrellaAiApps([]);
        setUmbrellaAppsSource('msp-wide');
      }
    } else {
      setUmbrellaAllOrgs([]);
      setUmbrellaOrgCategories([]);
      setUmbrellaAvailableOrgs([]);
      setUmbrellaAiApps([]);
    }

    setUmbrellaLoading(false);
  };

  const saveUmbrellaOrgMatch = async (orgId) => {
    const org = umbrellaAvailableOrgs.find(o => o.id === Number(orgId));
    if (!org) return;
    setSavingOrgMatch(true);
    try {
      await aiGovApi.updateProgram(programId, {
        ...editForm, tier_id: parseInt(editForm.tier_id),
        umbrella_org_id: String(org.id),
        umbrella_org_name: org.name,
      });
      setUmbrellaMatchedOrgId(org.id);
      setOrgPickerValue('');
      const orgRows = (umbrellaAllOrgs || []).filter(r => r.organizationId === org.id);
      setUmbrellaOrgCategories(orgRows);
      setUmbrellaSubTab(orgRows.length > 0 ? 'categories' : 'apps');
      toast.success(`Linked to ${org.name}`);
      load();
    } catch {
      toast.error('Failed to save org link');
    } finally {
      setSavingOrgMatch(false);
    }
  };

  const saveMergeFields = async () => {
    setSavingMergeFields(true);
    try {
      await aiGovApi.updateProgram(programId, {
        tier_id: program.tier_id,
        status: program.status,
        next_assessment_type: program.next_assessment_type,
        umbrella_org_id: program.umbrella_org_id || null,
        umbrella_org_name: program.umbrella_org_name || null,
        notes: program.notes || null,
        merge_fields: mergeFields,
      });
      setMergeFieldsEditing(false);
      toast.success('Document fields saved');
    } catch {
      toast.error('Failed to save document fields');
    } finally {
      setSavingMergeFields(false);
    }
  };

  // Approved tools helpers
  const openAddTool = (prefill = {}) => {
    const initDomains = Array.isArray(prefill.domains) ? prefill.domains
      : prefill.primary_domain ? [prefill.primary_domain] : [];
    setToolForm({ ...prefill, domains: initDomains });
    setEditingToolId(null);
    setRegistrySearch(prefill.tool_name || '');
    setToolCustomMode(!prefill.registry_tool_id && !prefill.tool_name);
    setToolModalOpen(true);
  };
  const openEditTool = (tool) => {
    let doms = tool.domains;
    if (typeof doms === 'string') { try { doms = JSON.parse(doms); } catch { doms = []; } }
    if (!Array.isArray(doms)) doms = [];
    if (tool.primary_domain && !doms.includes(tool.primary_domain)) doms = [tool.primary_domain, ...doms];
    setToolForm({
      registry_tool_id: tool.registry_tool_id || null,
      tool_name: tool.tool_name,
      vendor: tool.vendor || '',
      primary_domain: doms[0] || tool.primary_domain || '',
      category: tool.category || '',
      use_description: tool.use_description || '',
      restrictions: tool.restrictions || '',
      tier: tool.tier || '',
      domains: doms,
    });
    setEditingToolId(tool.id);
    setRegistrySearch(tool.tool_name || '');
    setToolCustomMode(!tool.registry_tool_id);
    setToolModalOpen(true);
  };
  const saveTool = async () => {
    if (!toolForm.tool_name) { toast.error('Tool name is required'); return; }
    const domains = Array.isArray(toolForm.domains) ? toolForm.domains : [];
    const payload = {
      tool_name:       toolForm.tool_name,
      vendor:          toolForm.vendor || '',
      primary_domain:  domains[0] || toolForm.primary_domain || '',
      domains:         domains,
      category:        toolForm.category || '',
      use_description: toolForm.use_description || '',
      restrictions:    toolForm.restrictions || '',
      tier:            toolForm.tier || null,
    };
    setSavingTool(true);
    try {
      if (editingToolId) {
        const res = await aiGovApi.updateApprovedTool(programId, editingToolId, payload);
        setApprovedTools(prev => prev.map(t => t.id === editingToolId ? res.data.tool : t));
      } else {
        const res = await aiGovApi.addApprovedTool(programId, payload);
        setApprovedTools(prev => [...prev, res.data.tool]);
      }
      setToolModalOpen(false);
      setToolForm({});
      setEditingToolId(null);
    } catch {
      toast.error('Failed to save tool');
    } finally {
      setSavingTool(false);
    }
  };
  const removeTool = async (toolId, name) => {
    if (!window.confirm(`Permanently remove "${name}"? To keep history, use Disapprove instead.`)) return;
    try {
      await aiGovApi.deleteApprovedTool(programId, toolId);
      setApprovedTools(prev => prev.filter(t => t.id !== toolId));
    } catch {
      toast.error('Failed to remove tool');
    }
  };

  const disapproveTool = async (toolId, name) => {
    try {
      const res = await aiGovApi.patchApprovedTool(programId, toolId, { status: 'disapproved' });
      setApprovedTools(prev => prev.map(t => t.id === toolId ? res.data.tool : t));
      toast.success(`"${name}" disapproved`);
    } catch {
      toast.error('Failed to disapprove tool');
    }
  };

  const reApproveTool = async (toolId, name) => {
    try {
      const res = await aiGovApi.patchApprovedTool(programId, toolId, { status: 'approved' });
      setApprovedTools(prev => prev.map(t => t.id === toolId ? res.data.tool : t));
      toast.success(`"${name}" re-approved`);
    } catch {
      toast.error('Failed to re-approve tool');
    }
  };

  const quickDisapproveCatalogTool = async (ct) => {
    let cd = ct.domains;
    if (typeof cd === 'string') { try { cd = JSON.parse(cd); } catch { cd = []; } }
    if (!Array.isArray(cd)) cd = [];
    try {
      const res = await aiGovApi.addApprovedTool(programId, {
        registry_tool_id: ct.id, tool_name: ct.name, vendor: ct.vendor,
        primary_domain: cd[0] || '', domains: cd, category: ct.category,
        status: 'disapproved',
      });
      setApprovedTools(prev => [...prev, res.data.tool]);
      toast.success(`"${ct.name}" marked as disapproved`);
    } catch {
      toast.error('Failed to disapprove tool');
    }
  };

  const logFeatureComplete = async (taskObj) => {
    if (!taskObj) return;
    try {
      await aiGovApi.completeTask(programId, taskObj.id, completeNotes);
      toast.success('Marked complete — next task scheduled');
      setCompletingFeature(null);
      setCompleteNotes('');
      const res = await aiGovApi.getProgram(programId);
      setProgram(res.data);
    } catch { toast.error('Failed to mark complete'); }
  };

  const loadUmbrellaIdentities = async () => {
    const orgId = umbrellaMatchedOrgId || program?.umbrella_org_id;
    if (!orgId) { toast.error('No Umbrella org matched — select one from the dropdown first'); return; }
    setIdentitiesLoading(true);
    try {
      const { from, to } = getUmbrellaDateParams();
      const params = { from, to };
      if (selectedCategories.length > 0) params.categories = selectedCategories.join(',');
      const res = await aiGovApi.getUmbrellaIdentitySummary(orgId, params);
      // Backend returns org_summary (requests-by-org for this org) — store the whole response
      setUmbrellaIdentities(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load org summary');
      setUmbrellaIdentities({});
    } finally {
      setIdentitiesLoading(false);
    }
  };

  const loadUmbrellaUserActivity = async () => {
    const orgId = umbrellaMatchedOrgId || program?.umbrella_org_id;
    if (!orgId) { toast.error('No Umbrella org matched'); return; }
    setUserActivityLoading(true);
    try {
      const { from, to } = getUmbrellaDateParams();
      const params = { from, to };
      if (selectedCategories.length > 0) params.categories = selectedCategories.join(',');
      const res = await aiGovApi.getUmbrellaUserActivity(orgId, params);
      setUmbrellaUserActivity(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load user activity');
      setUmbrellaUserActivity({ identities: [] });
    } finally {
      setUserActivityLoading(false);
    }
  };

  const toggleCategory = (id) => {
    setSelectedCategories(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Normalize the categories field on a row to an array of {id, label}
  const rowCategoryIds = (row) => {
    const cats = row.categories;
    if (!cats) return [];
    if (Array.isArray(cats)) return cats.map(c => typeof c === 'object' ? c.id : c);
    if (typeof cats === 'string') return cats.split(',').map(s => s.trim());
    return [];
  };

  const rowMatchesFilter = (row) => {
    if (selectedCategories.length === 0) return true;
    const ids = rowCategoryIds(row);
    return ids.some(id => selectedCategories.includes(id) || selectedCategories.includes(String(id)) || selectedCategories.includes(Number(id)));
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80, color: '#7E4DB3' }}>Loading…</div>
  );
  if (!program) return null;

  const sc = STATUS_COLORS[program.status] || STATUS_COLORS.active;
  const features = typeof program.features === 'string'
    ? JSON.parse(program.features || '[]')
    : (program.features || []);

  return (
    <div>
      {/* Back + Header */}
      <button onClick={() => navigate('/ai-gov')} style={{ background: 'none', border: 'none', color: P, cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
        ← AI Governance
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: PD, fontSize: 28, fontWeight: 700 }}>{program.client_name}</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
            <span style={{ background: sc.bg, color: sc.text, borderRadius: 12, padding: '2px 12px', fontSize: 13, fontWeight: 600 }}>
              {program.status}
            </span>
            <span style={{ color: '#7E4DB3', fontSize: 14 }}>{program.tier_name} Tier</span>
            <span style={{ color: '#9CA3AF', fontSize: 13 }}>Started {fmtDate(program.start_date)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowEdit(true)} style={outlineBtn}>Edit Program</button>
          <button
            onClick={() => handleStartAssessment(program.next_assessment_type)}
            style={primaryBtn}
            disabled={triggering}
          >
            {triggering ? 'Creating…' : `Start ${program.next_assessment_type === 'full' ? 'Full' : 'Core'} Assessment`}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `2px solid #E5D5F5`, marginBottom: 24 }}>
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: 15,
              fontWeight: activeTab === tab ? 700 : 500,
              color: activeTab === tab ? PD : '#7E4DB3',
              borderBottom: activeTab === tab ? `3px solid ${P}` : '3px solid transparent',
              background: 'none', marginBottom: -2,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {activeTab === 'Overview' && (() => {
        // Group tasks by task_type → { active (pending/due/overdue), lastDone }
        const tasksByType = {};
        (program.tasks || []).forEach(t => {
          if (!tasksByType[t.task_type]) tasksByType[t.task_type] = { active: null, lastDone: null };
          const entry = tasksByType[t.task_type];
          if (t.status === 'completed') {
            if (!entry.lastDone || new Date(t.completed_at) > new Date(entry.lastDone.completed_at)) {
              entry.lastDone = t;
            }
          } else {
            if (!entry.active || new Date(t.due_date) < new Date(entry.active.due_date)) {
              entry.active = t;
            }
          }
        });

        const FEATURE_TASK_MAP = {
          // Policy / contracts
          'AI Acceptable Use Policy': 'aup_review',
          'Acceptable Use Policy': 'aup_review',
          'AUP': 'aup_review',
          'MSA AI Addendum': 'msa_addendum_review',
          'MSA Addendum': 'msa_addendum_review',
          // Governance reviews
          'Quarterly Governance Review': 'governance_review',
          'Governance Review': 'governance_review',
          'Monthly cadence': 'governance_review',
          'Cadence review': 'governance_review',
          'Full governance report': 'governance_report_task',
          'Governance report': 'governance_report_task',
          // Monitoring / technical
          'Umbrella DNS': 'umbrella_review',
          'DNS Review': 'umbrella_review',
          'DNS monitoring': 'umbrella_review',
          'AI discovery': 'discovery_scan',
          'Discovery scan': 'discovery_scan',
          // Vendor / risk / training
          'Vendor due diligence': 'vendor_review',
          'Vendor DD': 'vendor_review',
          'Risk register': 'risk_register',
          'Training': 'training_review',
          // vCISO
          'vCISO': 'vciso_session',
          'vCISO AI Strategy': 'vciso_session',
        };

        // NIST assessments map to program.assessments, not cadence tasks
        const lastAssessment = (program.assessments || [])
          .filter(a => a.status === 'completed' && (a.type_code || '').includes('NIST-AI-RMF'))
          .sort((a, b) => new Date(b.completed_at || b.created_at) - new Date(a.completed_at || a.created_at))[0];

        const findTaskForFeature = (f) => {
          const lower = (f || '').toLowerCase();
          // Special case: NIST assessments link to the assessments list
          if (lower.includes('nist') || lower.includes('full assessment') || lower.includes('core assessment')) {
            return { type: '__assessment__', taskObj: null, lastDone: lastAssessment ? (lastAssessment.completed_at || lastAssessment.created_at) : null, nextDue: null };
          }
          for (const [kw, type] of Object.entries(FEATURE_TASK_MAP)) {
            if (lower.includes(kw.toLowerCase())) {
              const entry = tasksByType[type] || {};
              return {
                type,
                taskObj: entry.active || null,
                lastDone: entry.lastDone?.completed_at || null,
                nextDue: entry.active?.due_date || null,
                isOverdue: entry.active?.status === 'overdue',
              };
            }
          }
          return null;
        };

        const assessments = program.assessments || [];
        const ASSESS_STATUS = {
          completed: { bg: '#D1FAE5', text: '#065F46' },
          in_progress: { bg: '#EFF6FF', text: '#1E40AF' },
          draft: { bg: '#F3F4F6', text: '#6B7280' },
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Overview Report Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={async () => {
                  setGeneratingOverviewReport(true);
                  try {
                    const resp = await aiGovApi.generateOverviewReport(program.id);
                    const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${program.client_name || 'Client'}_AI_Program_Overview_${new Date().toISOString().slice(0,10)}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    toast.error('Failed to generate program overview report');
                  } finally {
                    setGeneratingOverviewReport(false);
                  }
                }}
                style={{ ...outlineBtn, borderColor: '#6B3FA0', color: '#6B3FA0' }}
                disabled={generatingOverviewReport}
                title="Download Program Overview Report PDF"
              >
                {generatingOverviewReport ? 'Generating…' : '↓ Program Overview Report'}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <Card title="Program Details">
                <Row label="Tier">{program.tier_name}</Row>
                <Row label="Monthly Price">${program.monthly_price}/mo</Row>
                <Row label="Status">{program.status}</Row>
                <Row label="Start Date">{fmtDate(program.start_date)}</Row>
                <Row label="Next Assessment">{program.next_assessment_type === 'full' ? 'Full (72 questions)' : 'Core (27 questions)'}</Row>
                {program.notes && <Row label="Notes">{program.notes}</Row>}
              </Card>

              <Card title="Tier Features">
                {/* Quick-complete modal */}
                {completingFeature && (
                  <div style={{ background: '#F3EEFA', border: '1px solid #D1C4E9', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: PD, marginBottom: 6 }}>Log completion: {completingFeature.label}</div>
                    <textarea
                      rows={2}
                      placeholder="Notes (optional)"
                      value={completeNotes}
                      onChange={e => setCompleteNotes(e.target.value)}
                      style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: 5, boxSizing: 'border-box', resize: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={() => logFeatureComplete(completingFeature.taskObj)} style={{ ...primaryBtn, fontSize: 12, padding: '4px 14px' }}>Mark Complete</button>
                      <button onClick={() => { setCompletingFeature(null); setCompleteNotes(''); }} style={{ ...outlineBtn, fontSize: 12, padding: '4px 14px' }}>Cancel</button>
                    </div>
                  </div>
                )}
                {features.length > 0 ? (
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {features.map((f, i) => {
                      const info = findTaskForFeature(f);
                      const done = !!info?.lastDone;
                      const canComplete = info?.taskObj && info.type !== '__assessment__';
                      const isOverdue = info?.isOverdue;
                      const circleColor = done ? '#10B981' : isOverdue ? '#EF4444' : info?.nextDue ? '#F59E0B' : '#D1D5DB';
                      const circleBg   = done ? '#D1FAE5' : isOverdue ? '#FEE2E2' : info?.nextDue ? '#FFFBEB' : '#F3F4F6';
                      return (
                        <li key={i} style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <span
                            onClick={() => canComplete && setCompletingFeature({ label: f, taskObj: info.taskObj })}
                            title={canComplete ? 'Click to log completion' : done ? 'Completed' : 'No active task'}
                            style={{
                              width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                              background: circleBg, border: `2px solid ${circleColor}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10,
                              cursor: canComplete ? 'pointer' : 'default',
                              color: done ? '#065F46' : isOverdue ? '#991B1B' : '#92400E',
                            }}
                          >
                            {done ? '✓' : isOverdue ? '!' : ''}
                          </span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, color: '#374151' }}>{f}</div>
                            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                              {done && <span>Last done {fmtDate(info.lastDone)}</span>}
                              {info?.nextDue && <span style={{ color: isOverdue ? '#EF4444' : '#6B7280' }}>
                                {isOverdue ? '⚠ Overdue since ' : 'Next due '}{fmtDate(info.nextDue)}
                              </span>}
                              {canComplete && !done && !isOverdue && <span style={{ color: '#6B3FA0', cursor: 'pointer' }} onClick={() => setCompletingFeature({ label: f, taskObj: info.taskObj })}>→ Log completion</span>}
                              {info?.type === '__assessment__' && done && <span style={{ color: '#6B7280' }}>See Assessment History below</span>}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p style={{ color: '#9CA3AF', fontSize: 14 }}>No features listed.</p>
                )}
              </Card>
            </div>

            {/* Document Merge Fields */}
            <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5D5F5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: PD }}>Document Merge Fields</h3>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>These values are inserted into generated documents using placeholders like <code style={{ background: '#F3F4F6', padding: '1px 4px', borderRadius: 3 }}>{'{{CLIENT_NAME}}'}</code></p>
                </div>
                {mergeFieldsEditing ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setMergeFieldsEditing(false)} style={{ ...outlineBtn, fontSize: 13, padding: '5px 14px' }}>Cancel</button>
                    <button onClick={saveMergeFields} style={{ ...primaryBtn, fontSize: 13, padding: '5px 14px' }} disabled={savingMergeFields}>
                      {savingMergeFields ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setMergeFieldsEditing(true)} style={{ ...outlineBtn, fontSize: 13, padding: '5px 14px' }}>Edit</button>
                )}
              </div>
              <div style={{ padding: '16px 20px' }}>
                {(() => {
                  // Keys filled automatically by the system — never shown to the assessor
                  const isAutoKey = key => {
                    const k = (key || '').toLowerCase();
                    if (['client_name','ir_contact_name','contact_name','ir_contact_email','contact_email',
                         'start_date','today','tier','document_version','vendor_name',
                         'review_date','review_trigger','reviewer_name'].includes(k)) return true;
                    if (/^tool_\d+_/.test(k)) return true; // tool inventory rows (tool_1_name, tool_2_vendor…)
                    return false;
                  };
                  // Keys filled by the vendor due-diligence workflow, not by the assessor here
                  const isVendorDDKey = key => {
                    const k = (key || '').toLowerCase();
                    return /^(baa|dpa|residency|subprocessor|breach_notice|certifications|determination|training_opt_out)(_|$)/.test(k);
                  };

                  const seenKeys = new Set();
                  const manualFields = [];
                  let autoCount = 0;
                  let vendorDDCount = 0;

                  templates.forEach(tmpl => {
                    let mf = tmpl.merge_fields;
                    if (typeof mf === 'string') { try { mf = JSON.parse(mf); } catch { mf = []; } }
                    if (!Array.isArray(mf)) return;
                    mf.forEach(f => {
                      if (seenKeys.has(f.key)) return;
                      seenKeys.add(f.key);
                      if (isAutoKey(f.key) || f.auto) { autoCount++; return; }
                      if (isVendorDDKey(f.key))        { vendorDDCount++; return; }
                      manualFields.push({
                        key:   f.key,
                        label: f.label || f.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                        token: `{{${f.key}}}`,
                      });
                    });
                  });

                  // Fallback when no templates have been scanned yet
                  if (manualFields.length === 0 && autoCount === 0 && vendorDDCount === 0) {
                    [
                      { key: 'policy_owner_name',      label: 'Policy Owner Name' },
                      { key: 'effective_date',          label: 'Policy Effective Date' },
                      { key: 'last_reviewed_date',      label: 'Last Reviewed Date' },
                      { key: 'intake_request_channel',  label: 'Intake Request Channel' },
                    ].forEach(f => manualFields.push({ ...f, token: `{{${f.key}}}` }));
                  }

                  return (
                    <div>
                      {/* System-handled notice */}
                      {(autoCount > 0 || vendorDDCount > 0) && (
                        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#166534', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                          {autoCount > 0 && <span>✓ <strong>{autoCount} fields</strong> auto-filled from client &amp; program data</span>}
                          {vendorDDCount > 0 && <span>✓ <strong>{vendorDDCount} fields</strong> filled by the vendor due-diligence workflow</span>}
                        </div>
                      )}

                      {manualFields.length > 0 ? (
                        <div>
                          <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Needs your input ({manualFields.length})
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px 24px' }}>
                            {manualFields.map(f => (
                              <div key={f.key}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>{f.label}</span>
                                  <code style={{ fontWeight: 400, color: '#9CA3AF', fontSize: 10 }}>{f.token}</code>
                                </div>
                                {mergeFieldsEditing ? (
                                  <input
                                    type="text"
                                    value={mergeFields[f.key] || ''}
                                    placeholder={f.placeholder || ''}
                                    onChange={e => setMergeFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                                  />
                                ) : (
                                  <div style={{ fontSize: 13, color: mergeFields[f.key] ? '#111827' : '#D1D5DB', padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                                    {mergeFields[f.key] || <em>not set</em>}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>All fields in the uploaded templates are auto-populated. No manual input needed.</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Approved AI Tools */}
            {(() => {
              // Build a set of observed root domains from Umbrella AI apps data
              const observedRoots = new Set();
              if (umbrellaAiApps) {
                umbrellaAiApps.forEach(r => {
                  const d = r.domain || r.destination || r.name || '';
                  if (d) observedRoots.add(getRootDomain(d));
                });
              }
              // Track domains/names for approved-only tools (status='approved')
              const approvedDomainSet = new Set();
              const approvedNameSet = new Set();
              // Track names that have been explicitly disapproved (status='disapproved')
              const disapprovedNameSet = new Set();
              approvedTools.forEach(t => {
                if (t.status === 'disapproved') { disapprovedNameSet.add(t.tool_name); return; }
                approvedNameSet.add(t.tool_name);
                let doms = t.domains;
                if (typeof doms === 'string') { try { doms = JSON.parse(doms); } catch { doms = []; } }
                if (!Array.isArray(doms)) doms = [];
                if (t.primary_domain) approvedDomainSet.add(getRootDomain(t.primary_domain));
                doms.forEach(d => approvedDomainSet.add(getRootDomain(d)));
              });
              // Registry entries not yet approved/disapproved, matched to observed domains
              const suggestions = toolRegistry.filter(ct => {
                let ctDoms = ct.domains;
                if (typeof ctDoms === 'string') { try { ctDoms = JSON.parse(ctDoms); } catch { ctDoms = []; } }
                if (!Array.isArray(ctDoms)) ctDoms = [];
                const alreadyHandled = ctDoms.some(d => approvedDomainSet.has(getRootDomain(d)))
                  || approvedNameSet.has(ct.name)
                  || disapprovedNameSet.has(ct.name);
                const detected = ctDoms.some(d => observedRoots.has(getRootDomain(d)));
                return detected && !alreadyHandled;
              });

              return (
                <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5D5F5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: PD }}>Approved AI Tools</h3>
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>Tools approved for use by this organization — inserted into generated AUP documents</p>
                    </div>
                    <button onClick={() => openAddTool()} style={{ ...primaryBtn, fontSize: 13, padding: '5px 14px' }}>+ Add Tool</button>
                  </div>

                  {/* Detected-but-not-approved suggestions from Umbrella */}
                  {suggestions.length > 0 && (
                    <div style={{ padding: '10px 20px', background: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}>
                      <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#92400E' }}>
                        Detected in Umbrella DNS — not yet approved:
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {suggestions.map(ct => (
                          <div key={ct.name} style={{ display: 'flex', alignItems: 'center', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid #FCD34D' }}>
                            <button
                              onClick={() => { let d = ct.domains; if (typeof d === 'string') { try { d = JSON.parse(d); } catch { d = []; } } openAddTool({ registry_tool_id: ct.id, tool_name: ct.name, vendor: ct.vendor, primary_domain: d[0], domains: d, category: ct.category }); }}
                              style={{ background: '#FEF3C7', border: 'none', borderRight: '1px solid #FCD34D', padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#78350F', display: 'flex', alignItems: 'center', gap: 5 }}
                            >
                              <span style={{ background: '#F59E0B', color: 'white', borderRadius: 3, padding: '1px 4px', fontSize: 9, fontWeight: 700 }}>DETECTED</span>
                              {ct.name}
                              <span style={{ color: '#15803D', fontWeight: 600 }}>✓ Approve</span>
                            </button>
                            <button
                              onClick={() => quickDisapproveCatalogTool(ct)}
                              title="Mark as disapproved — won't be added to AUP"
                              style={{ background: '#FEF2F2', border: 'none', padding: '4px 8px', fontSize: 12, cursor: 'pointer', color: '#B91C1C', fontWeight: 600 }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Approved tools list */}
                  {approvedTools.length === 0 ? (
                    <div style={{ padding: '28px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                      No approved tools yet. Add tools manually or approve detected tools above.
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5D5F5' }}>
                          {['#', 'Tool', 'Vendor', 'Tier', 'Status', 'Domain', 'Approved Use', 'Restrictions', ''].map(h => (
                            <th key={h} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, color: '#6B7280', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {approvedTools.map((tool, idx) => {
                          const detected = tool.primary_domain && observedRoots.has(getRootDomain(tool.primary_domain));
                          const isDisapproved = tool.status === 'disapproved';
                          return (
                            <tr key={tool.id} style={{ borderBottom: '1px solid #F3F4F6', opacity: isDisapproved ? 0.65 : 1, background: isDisapproved ? '#FFF7F7' : 'transparent' }}>
                              <td style={{ padding: '8px 12px', fontSize: 13, color: '#9CA3AF', width: 32 }}>{idx + 1}</td>
                              <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  {tool.tool_name}
                                  {detected && !isDisapproved && <span style={{ background: '#D1FAE5', color: '#065F46', borderRadius: 3, padding: '1px 4px', fontSize: 9, fontWeight: 700 }}>DETECTED</span>}
                                </div>
                                {tool.category && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{tool.category}</div>}
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: 13, color: '#374151' }}>{tool.vendor || '—'}</td>
                              <td style={{ padding: '8px 12px', fontSize: 12 }}>
                                {tool.tier
                                  ? <span style={{ background: tool.tier === 'Enterprise' ? '#EDE9FE' : tool.tier === 'Paid' ? '#DBEAFE' : '#F3F4F6', color: tool.tier === 'Enterprise' ? '#5B21B6' : tool.tier === 'Paid' ? '#1D4ED8' : '#6B7280', borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 600 }}>{tool.tier}</span>
                                  : <span style={{ color: '#D1D5DB', fontSize: 12 }}>—</span>
                                }
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: 12 }}>
                                {isDisapproved
                                  ? <span style={{ background: '#FEE2E2', color: '#991B1B', borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>✕ Disapproved</span>
                                  : <span style={{ background: '#D1FAE5', color: '#065F46', borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>✓ Approved</span>
                                }
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: 12, color: '#6B7280', fontFamily: 'monospace' }}>{tool.primary_domain || '—'}</td>
                              <td style={{ padding: '8px 12px', fontSize: 13, color: '#374151', maxWidth: 220 }}>{tool.use_description || <em style={{ color: '#D1D5DB' }}>not set</em>}</td>
                              <td style={{ padding: '8px 12px', fontSize: 13, color: '#374151', maxWidth: 180 }}>{tool.restrictions || <em style={{ color: '#D1D5DB' }}>none</em>}</td>
                              <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                {!isDisapproved && (
                                  <button onClick={() => openEditTool(tool)} style={{ ...outlineBtn, fontSize: 12, padding: '3px 10px', marginRight: 6 }}>Edit</button>
                                )}
                                {isDisapproved ? (
                                  <>
                                    <button onClick={() => reApproveTool(tool.id, tool.tool_name)} style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', color: '#065F46', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Re-approve</button>
                                    <button onClick={() => removeTool(tool.id, tool.tool_name)} style={{ background: 'none', border: '1px solid #FCA5A5', color: '#EF4444', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>Delete</button>
                                  </>
                                ) : (
                                  <button onClick={() => disapproveTool(tool.id, tool.tool_name)} style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>Disapprove</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()}

            {/* Assessment History */}
            <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5D5F5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: PD }}>Assessment History</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleStartAssessment('full')} style={{ ...primaryBtn, fontSize: 13, padding: '5px 14px' }} disabled={triggering}>
                    + Full Assessment
                  </button>
                  <button onClick={() => handleStartAssessment('core')} style={{ ...outlineBtn, fontSize: 13, padding: '5px 14px' }} disabled={triggering}>
                    + Core Assessment
                  </button>
                </div>
              </div>
              {assessments.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                  No assessments completed yet. Start a Full or Core NIST AI RMF assessment above.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: PL }}>
                      {['Assessment', 'Type', 'Status', 'Score', 'Date', ''].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {assessments.map((a, i) => {
                      const sc = ASSESS_STATUS[a.status] || ASSESS_STATUS.draft;
                      const score = a.percentage_score ?? a.score;
                      const isNist = (a.type_code || '').includes('NIST-AI-RMF');
                      return (
                        <tr key={a.id} style={{ borderTop: '1px solid #F3EEFA', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                          <td style={{ padding: '12px 16px', fontWeight: 600, color: PD }}>{a.name}</td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280' }}>
                            {isNist ? (a.type_code?.includes('CORE') ? 'Core (27q)' : 'Full (72q)') : (a.type_name || '—')}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ background: sc.bg, color: sc.text, borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>
                              {a.status?.replace('_', ' ')}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600, color: score != null ? (score >= 70 ? '#065F46' : score >= 40 ? '#92400E' : '#991B1B') : '#9CA3AF' }}>
                            {score != null ? `${Math.round(score)}%` : '—'}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280' }}>{fmtDate(a.created_at)}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <button
                              onClick={() => navigate(`/assessments/${a.id}`)}
                              style={{ ...outlineBtn, padding: '4px 12px', fontSize: 12 }}
                            >
                              View →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <Card title="Quick Actions">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button onClick={() => setActiveTab('Documents')} style={{ ...outlineBtn, textAlign: 'left' }}>
                    Manage Documents →
                  </button>
                  <button onClick={() => setActiveTab('Tasks')} style={{ ...outlineBtn, textAlign: 'left' }}>
                    View Cadence Tasks →
                  </button>
                  <button onClick={() => setActiveTab('Umbrella')} style={{ ...outlineBtn, textAlign: 'left' }}>
                    Umbrella AI Monitoring →
                  </button>
                </div>
              </Card>

              <Card title="Umbrella Config">
                {program.umbrella_org_id ? (
                  <>
                    <Row label="Org ID">{program.umbrella_org_id}</Row>
                    <Row label="Org Name">{program.umbrella_org_name || '—'}</Row>
                    <button onClick={() => setActiveTab('Umbrella')} style={{ ...outlineBtn, marginTop: 10, fontSize: 13, padding: '5px 12px' }}>
                      View AI Activity →
                    </button>
                  </>
                ) : (
                  <p style={{ color: '#9CA3AF', fontSize: 14 }}>
                    No Umbrella organization linked.{' '}
                    <button onClick={() => setShowEdit(true)} style={{ background: 'none', border: 'none', color: P, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                      Configure
                    </button>
                  </p>
                )}
              </Card>
            </div>
          </div>
        );
      })()}

      {/* ── Documents ── */}
      {activeTab === 'Documents' && (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
            <select
              value={genTemplateId}
              onChange={e => setGenTemplateId(e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
            >
              <option value="">Select a template to generate…</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.type})</option>
              ))}
            </select>
            <button onClick={handleGenerateDoc} style={primaryBtn} disabled={generating || !genTemplateId}>
              {generating ? 'Generating…' : 'Generate Document'}
            </button>
          </div>

          {program.documents?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', background: 'white', borderRadius: 8, border: '1px solid #E5D5F5' }}>
              No documents generated yet.
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: PL }}>
                    {['Document', 'Template', 'Status', 'Generated', ''].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(program.documents || []).map((doc, i) => {
                    const ds = DOC_STATUS_COLORS[doc.status] || DOC_STATUS_COLORS.draft;
                    return (
                      <tr key={doc.id} style={{ borderTop: '1px solid #F3EEFA', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: PD }}>{doc.name}</td>
                        <td style={{ padding: '12px 16px', fontSize: 14, color: '#374151' }}>{doc.template_name}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <select
                            value={doc.status}
                            onChange={e => handleDocStatus(doc.id, e.target.value)}
                            style={{ background: ds.bg, color: ds.text, border: 'none', borderRadius: 12, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          >
                            {['draft', 'sent', 'signed', 'archived'].map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280' }}>{fmtDate(doc.generated_at)}</td>
                        <td style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button onClick={() => handleDownloadDoc(doc.id)} style={{ ...outlineBtn, padding: '4px 12px', fontSize: 12 }}>
                            Download
                          </button>
                          <button
                            onClick={() => handleDeleteDoc(doc.id, doc.name)}
                            style={{ padding: '4px 12px', fontSize: 12, borderRadius: 6, border: '1px solid #FECACA', background: 'white', color: '#991B1B', cursor: 'pointer', fontWeight: 600 }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tasks ── */}
      {activeTab === 'Tasks' && (() => {
          const today = new Date(); today.setHours(0,0,0,0);
          const soon  = new Date(today); soon.setDate(today.getDate() + 7);

          const withStatus = (program.tasks || []).map(t => {
            let effectiveStatus = t.status;
            if (t.status !== 'completed') {
              const due = t.due_date ? new Date(String(t.due_date).split('T')[0] + 'T00:00:00') : null;
              if (due) {
                if (due < today)      effectiveStatus = 'overdue';
                else if (due <= soon) effectiveStatus = 'due';
                else                  effectiveStatus = 'pending';
              }
            }
            return { ...t, effectiveStatus };
          });

          const openTasks   = withStatus.filter(t => t.effectiveStatus !== 'completed');
          const closedTasks = withStatus.filter(t => t.effectiveStatus === 'completed');
          const displayed   = showTaskHistory ? withStatus : openTasks;

          const overdueCount = openTasks.filter(t => t.effectiveStatus === 'overdue').length;
          const dueCount     = openTasks.filter(t => t.effectiveStatus === 'due').length;

          const statusColors = {
            overdue: { bg: '#FEE2E2', text: '#991B1B' },
            due:     { bg: '#FEF3C7', text: '#92400E' },
            pending: { bg: '#EDE9FE', text: '#5B21B6' },
            completed: { bg: '#D1FAE5', text: '#065F46' },
          };

          return (
            <div>
              {/* Summary bar */}
              {(overdueCount > 0 || dueCount > 0) && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                  {overdueCount > 0 && (
                    <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#991B1B', fontWeight: 600 }}>
                      ⚠ {overdueCount} overdue task{overdueCount > 1 ? 's' : ''}
                    </div>
                  )}
                  {dueCount > 0 && (
                    <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#92400E', fontWeight: 600 }}>
                      ⏰ {dueCount} due this week
                    </div>
                  )}
                </div>
              )}

              {/* Table header + history toggle */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: '#6B7280' }}>
                  {showTaskHistory ? `All tasks (${withStatus.length})` : `Open tasks (${openTasks.length})`}
                </span>
                <button onClick={() => setShowTaskHistory(p => !p)}
                  style={{ background: 'none', border: 'none', color: P, cursor: 'pointer', fontSize: 13, fontWeight: 600, textDecoration: 'underline' }}>
                  {showTaskHistory ? 'Hide completed' : `Show history (${closedTasks.length} completed)`}
                </button>
              </div>

              {displayed.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', background: 'white', borderRadius: 8, border: '1px solid #E5D5F5' }}>
                  {openTasks.length === 0 ? 'All tasks completed.' : 'No cadence tasks found.'}
                </div>
              ) : (
                <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: PL }}>
                        {['Task', 'Cadence', 'Status', 'Due Date', 'Completed / CW', ''].map(h => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayed.map((t, i) => {
                        const sc = statusColors[t.effectiveStatus] || statusColors.pending;
                        const hasSchedule = !!t.ticket_schedule_id;
                        return (
                          <tr key={t.id} style={{ borderTop: '1px solid #F3EEFA', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ fontWeight: 600, color: PD }}>{t.title}</div>
                              {t.description && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{t.description}</div>}
                              {hasSchedule && (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, background: '#EEF2FF', borderRadius: 4, padding: '1px 6px', fontSize: 11, color: '#4338CA', fontWeight: 600 }}>
                                  🔁 CW scheduled
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151', textTransform: 'capitalize' }}>{t.cadence}</td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{ background: sc.bg, color: sc.text, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                                {t.effectiveStatus}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: 13, color: t.effectiveStatus === 'overdue' ? '#DC2626' : '#374151', fontWeight: t.effectiveStatus === 'overdue' ? 600 : 400 }}>
                              {fmtDate(t.due_date)}
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280' }}>
                              {t.completed_at
                                ? `${fmtDate(t.completed_at)} by ${t.completed_by_name || 'unknown'}`
                                : t.cw_ticket_id
                                  ? <span style={{ color: '#6366F1', fontWeight: 600 }}>CW #{t.cw_ticket_id}</span>
                                  : '—'}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {t.effectiveStatus !== 'completed' && (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  <button onClick={() => setCompletingTask(t.id)}
                                    style={{ ...outlineBtn, padding: '3px 10px', fontSize: 12 }}>
                                    Complete
                                  </button>
                                  <button onClick={() => setSchedulingTask(t)}
                                    style={{ padding: '3px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                                      background: hasSchedule ? '#EEF2FF' : 'transparent',
                                      border: hasSchedule ? '2px solid #6366F1' : '2px solid #9CA3AF',
                                      color: hasSchedule ? '#4338CA' : '#6B7280' }}>
                                    {hasSchedule ? '🔁 Schedule' : 'Schedule'}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

      {/* ── Activity ── */}
      {activeTab === 'Activity' && (
        <div>
          {/* Header + controls */}
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 2 }}>Umbrella AI Monitoring</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: PD }}>
                  {umbrellaMatchedOrgId
                    ? (umbrellaAvailableOrgs.find(o => o.id === umbrellaMatchedOrgId)?.name || program.umbrella_org_name || program.client_name)
                    : program.client_name}
                  {umbrellaMatchedOrgId && (
                    <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400, marginLeft: 8 }}>org {umbrellaMatchedOrgId}</span>
                  )}
                  {umbrellaMatchedOrgId && umbrellaAvailableOrgs.length > 0 && (
                    <button onClick={() => { setUmbrellaMatchedOrgId(null); setUmbrellaOrgCategories([]); setOrgPickerValue(''); }}
                      style={{ marginLeft: 10, fontSize: 11, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                      change
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={umbrellaDatePreset}
                  onChange={e => setUmbrellaDatePreset(e.target.value)}
                  style={{ ...inputStyle, marginBottom: 0, minWidth: 170, padding: '6px 10px', fontSize: 13 }}
                >
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="thisMonth">This Month</option>
                  <option value="custom">Custom…</option>
                </select>
                <span style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>
                  Umbrella retains granular DNS activity for 30 days
                </span>
                {umbrellaDatePreset === 'custom' && (
                  <>
                    <input
                      type="date"
                      value={umbrellaCustomFrom}
                      min={new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)}
                      onChange={e => setUmbrellaCustomFrom(e.target.value)}
                      style={{ ...inputStyle, marginBottom: 0, width: 145, padding: '6px 8px', fontSize: 13 }}
                    />
                    <span style={{ fontSize: 13, color: '#6B7280' }}>to</span>
                    <input
                      type="date"
                      value={umbrellaCustomTo}
                      onChange={e => setUmbrellaCustomTo(e.target.value)}
                      style={{ ...inputStyle, marginBottom: 0, width: 145, padding: '6px 8px', fontSize: 13 }}
                    />
                  </>
                )}
                <button
                  onClick={() => loadUmbrellaOrgData(selectedCategories)}
                  style={primaryBtn}
                  disabled={umbrellaLoading || (umbrellaDatePreset === 'custom' && !umbrellaCustomFrom)}
                >
                  {umbrellaLoading ? 'Loading…' : (umbrellaOrgCategories === null ? 'Load' : 'Refresh')}
                </button>
                <button
                  onClick={async () => {
                    setGeneratingReport(true);
                    try {
                      const { from, to } = getUmbrellaDateParams();
                      const resp = await aiGovApi.generateUmbrellaReport(program.id, from, to, selectedCategories);
                      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${program.client_name || 'Client'}_AI_Activity_Report_${new Date().toISOString().slice(0,10)}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (err) {
                      toast.error('Failed to generate activity report');
                    } finally {
                      setGeneratingReport(false);
                    }
                  }}
                  style={{ ...outlineBtn, borderColor: P, color: P }}
                  disabled={generatingReport || !program?.umbrella_org_id}
                  title={!program?.umbrella_org_id ? 'No Umbrella Org ID configured' : 'Download Activity Report PDF'}
                >
                  {generatingReport ? 'Generating…' : '↓ Activity Report'}
                </button>
                <button onClick={() => setShowEdit(true)} style={outlineBtn}>Edit Program</button>
              </div>
            </div>

            {/* Org picker — shown when no match resolved */}
            {umbrellaOrgCategories !== null && !umbrellaMatchedOrgId && umbrellaAvailableOrgs.length > 0 && (
              <div style={{ marginBottom: 14, padding: '12px 14px', background: '#FEF3C7', borderRadius: 6 }}>
                <div style={{ fontSize: 13, color: '#92400E', fontWeight: 600, marginBottom: 8 }}>
                  No Umbrella org matched "{program.client_name}" — select the correct org:
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    value={orgPickerValue}
                    onChange={e => setOrgPickerValue(e.target.value)}
                    style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                  >
                    <option value="">— Select Umbrella organization —</option>
                    {umbrellaAvailableOrgs.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => saveUmbrellaOrgMatch(orgPickerValue)}
                    disabled={!orgPickerValue || savingOrgMatch}
                    style={primaryBtn}
                  >
                    {savingOrgMatch ? 'Saving…' : 'Link Org'}
                  </button>
                </div>
              </div>
            )}

            {/* Category filter pills */}
            {umbrellaCategories.length > 0 && (
              <div>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                  Category filter <span style={{ color: '#9CA3AF' }}>({selectedCategories.length === 0 ? 'all' : `${selectedCategories.length} selected`})</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {umbrellaCategories.filter(c => !c.deprecated).map(c => {
                    const sel = selectedCategories.includes(c.id);
                    return (
                      <button key={c.id} onClick={() => setSelectedCategories(prev =>
                        prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id]
                      )} style={{
                        padding: '2px 9px', borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        background: sel ? P : 'white', color: sel ? 'white' : '#6B7280',
                        border: `1px solid ${sel ? P : '#D1D5DB'}`,
                      }}>{c.label || c.name}</button>
                    );
                  })}
                  <button onClick={() => setSelectedCategories([])} style={{ padding: '2px 9px', borderRadius: 14, fontSize: 11, background: 'none', border: '1px solid #E5E7EB', color: '#6B7280', cursor: 'pointer' }}>Clear</button>
                </div>
              </div>
            )}
          </div>

          {/* Sub-tabs — show once data is loaded */}
          {(umbrellaOrgCategories !== null || umbrellaAiApps !== null) && umbrellaMatchedOrgId && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
              {[
                ['categories', `AI Categories${umbrellaOrgCategories?.length > 0 ? ` (${[...new Set(umbrellaOrgCategories.map(r => r.category))].length})` : ''}`],
                ['apps', 'Applications'],
                ['users', 'Users & Devices'],
              ].map(([key, label]) => (
                <button key={key} onClick={() => {
                  setUmbrellaSubTab(key);
                  if (key === 'users') {
                    if (umbrellaIdentities === null) loadUmbrellaIdentities();
                    if (umbrellaUserActivity === null) loadUmbrellaUserActivity();
                  }
                }} style={{
                  padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: umbrellaSubTab === key ? P : '#F3EEFA',
                  color: umbrellaSubTab === key ? 'white' : PD,
                }}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* ── AI Categories (this org) ── */}
          {umbrellaSubTab === 'categories' && umbrellaOrgCategories !== null && (
            <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5D5F5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, color: PD, fontSize: 15, fontWeight: 700 }}>
                  AI Activity — {program.umbrella_org_name || program.client_name}
                </h4>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, color: '#6B7280' }}>
                    {umbrellaOrgCategories.length} categories · {umbrellaOrgCategories.reduce((s, r) => s + (r.requests || 0), 0).toLocaleString()} categorized requests
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                    A single request may appear in multiple categories
                  </div>
                </div>
              </div>
              {umbrellaOrgCategories.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                  No activity found for this org with the selected category filter.
                  {umbrellaAllOrgs?.length > 0 && <span> Switch to <button onClick={() => setUmbrellaSubTab('allOrgs')} style={{ background: 'none', border: 'none', color: P, cursor: 'pointer', fontWeight: 600 }}>All Organizations</button> to see MSP-wide data.</span>}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: PL }}>
                        {['Category', 'Requests', 'Blocked', '% of Total'].map(h => (
                          <th key={h} style={{ ...thStyle, textAlign: h !== 'Category' ? 'right' : 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const total = umbrellaOrgCategories.reduce((s, r) => s + (r.requests || 0), 0);
                        const aiKeywords = ['generative', 'conversational', 'code assist', 'image gen', 'machine learning', 'llm', 'openai', 'chatgpt'];
                        return umbrellaOrgCategories.map((row, i) => {
                          const isAi = aiKeywords.some(kw => (row.category || '').toLowerCase().includes(kw));
                          const pct = total > 0 ? ((row.requests / total) * 100).toFixed(1) : '0.0';
                          return (
                            <tr key={i} style={{ borderTop: '1px solid #F3EEFA', background: isAi ? '#FAF5FF' : (i % 2 === 0 ? 'white' : '#FAFAFA') }}>
                              <td style={{ padding: '10px 16px', fontWeight: isAi ? 700 : 400, color: isAi ? PD : '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {isAi && <span style={{ width: 6, height: 6, borderRadius: '50%', background: P, display: 'inline-block', flexShrink: 0 }} />}
                                {row.category || '—'}
                              </td>
                              <td style={{ padding: '10px 16px', fontSize: 13, color: '#374151', textAlign: 'right', fontWeight: 600 }}>{row.requests?.toLocaleString() ?? '—'}</td>
                              <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                                {row.requestsBlocked > 0
                                  ? <span style={{ color: '#991B1B', fontWeight: 600 }}>{row.requestsBlocked.toLocaleString()}</span>
                                  : <span style={{ color: '#9CA3AF' }}>0</span>}
                              </td>
                              <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                                  <div style={{ width: 60, height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: isAi ? P : '#9CA3AF', borderRadius: 3 }} />
                                  </div>
                                  <span style={{ fontSize: 12, color: '#6B7280', minWidth: 36, textAlign: 'right' }}>{pct}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── AI Applications (Tool-first view) ── */}
          {umbrellaSubTab === 'apps' && (
            <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
              {umbrellaAiApps === null ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>Loading…</div>
              ) : umbrellaAiApps.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                  No destination data found for this org in the selected date range.
                </div>
              ) : (() => {
                const domainFn = r => r.domain || r.destination || r.name || '—';
                const countFn  = r => r.requests ?? r.dnsRequests ?? r.count ?? 0;
                const getCats  = r => Array.isArray(r.categories)
                  ? r.categories.map(c => typeof c === 'object' ? (c.label || c.name) : c).join(', ')
                  : (r.category?.label || r.categoryLabel || r.category || '—');

                // root-domain → registry tool (build first so approved-tool expansion can use it)
                const catalogById = {};
                const catalogByRoot = {};
                toolRegistry.forEach(ct => {
                  catalogById[ct.id] = ct;
                  let ctd = ct.domains;
                  if (typeof ctd === 'string') { try { ctd = JSON.parse(ctd); } catch { ctd = []; } }
                  (Array.isArray(ctd) ? ctd : []).forEach(d => { catalogByRoot[getRootDomain(d)] = ct; });
                });

                // name → registry tool (for name-based fallback below)
                const catalogByName = {};
                toolRegistry.forEach(ct => { catalogByName[(ct.name || '').toLowerCase()] = ct; });

                // Build root-domain maps for approved and disapproved tools separately
                const approvedRootMap    = {};
                const disapprovedRootMap = {};
                approvedTools.forEach(t => {
                  let doms = t.domains;
                  if (typeof doms === 'string') { try { doms = JSON.parse(doms); } catch { doms = []; } }
                  if (!Array.isArray(doms)) doms = [];
                  if (t.primary_domain && !doms.includes(t.primary_domain)) doms = [t.primary_domain, ...doms];
                  const regTool = t.registry_tool_id
                    ? catalogById[t.registry_tool_id]
                    : catalogByName[(t.tool_name || '').toLowerCase()];
                  if (regTool) {
                    let regd = regTool.domains;
                    if (typeof regd === 'string') { try { regd = JSON.parse(regd); } catch { regd = []; } }
                    if (Array.isArray(regd)) doms = [...new Set([...doms, ...regd])];
                  }
                  const targetMap = t.status === 'disapproved' ? disapprovedRootMap : approvedRootMap;
                  doms.forEach(d => { targetMap[getRootDomain(d)] = t; });
                });

                // Group Umbrella rows by root domain first
                const domainGroups = groupByRootDomain(umbrellaAiApps, domainFn, countFn);

                // Then group root-domain-groups by tool key (approved tool id, disapproved tool id, catalog tool id, or 'unknown')
                const toolBuckets = {};
                const unknownBuckets = [];

                domainGroups.forEach(g => {
                  const approved    = approvedRootMap[g.root];
                  const disapproved = !approved && disapprovedRootMap[g.root];
                  const catalog     = !approved && !disapproved && catalogByRoot[g.root];
                  const bucketKey   = approved ? `approved-${approved.id}` : disapproved ? `disapproved-${disapproved.id}` : catalog ? `catalog-${catalog.id}` : null;

                  const domainEntry = {
                    root: g.root,
                    total: g.total,
                    totalBlocked: g.totalBlocked,
                    rows: g.rows,
                    topCats: g.rows[0] ? getCats(g.rows[0]) : '—',
                  };

                  if (bucketKey) {
                    if (!toolBuckets[bucketKey]) {
                      const toolRef = approved || disapproved;
                      toolBuckets[bucketKey] = {
                        key: bucketKey,
                        approved:    approved || null,
                        disapproved: disapproved || null,
                        catalog:     catalog || null,
                        toolName:    toolRef ? toolRef.tool_name : catalog?.name,
                        vendor:      toolRef ? (toolRef.vendor || '') : (catalog?.vendor || ''),
                        category:    toolRef ? (toolRef.category || '') : (catalog?.category || ''),
                        totalReqs: 0,
                        totalBlocked: 0,
                        domainEntries: [],
                      };
                    }
                    toolBuckets[bucketKey].totalReqs += g.total;
                    toolBuckets[bucketKey].totalBlocked += g.totalBlocked;
                    toolBuckets[bucketKey].domainEntries.push(domainEntry);
                  } else {
                    unknownBuckets.push(domainEntry);
                  }
                });

                const toolRows = Object.values(toolBuckets)
                  .sort((a, b) => b.totalReqs - a.totalReqs);
                const totalMatched = toolRows.reduce((s, t) => s + t.totalReqs, 0);
                const totalUnknown = unknownBuckets.reduce((s, g) => s + g.total, 0);

                return (
                  <>
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5D5F5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ margin: 0, color: PD, fontSize: 15, fontWeight: 700 }}>
                          AI Applications — {umbrellaAppsSource?.startsWith('child-org') ? (program?.umbrella_org_name || program?.client_name || 'This Client') : 'All Customers'}
                        </h4>
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                          {umbrellaAppsSource?.startsWith('child-org')
                            ? `Per-org data via Umbrella child token · org ${umbrellaAppsSource.replace('child-org-', '')}`
                            : 'MSP-wide data · per-org data unavailable'}
                        </p>
                      </div>
                      <span style={{ fontSize: 13, color: '#6B7280' }}>
                        {toolRows.length} tool{toolRows.length !== 1 ? 's' : ''} matched
                        {unknownBuckets.length > 0 && ` · ${unknownBuckets.length} unrecognized domain${unknownBuckets.length !== 1 ? 's' : ''}`}
                      </span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: PL }}>
                            {['Tool', 'Vendor', 'Category', 'Status', 'Requests', 'Blocked'].map(h => (
                              <th key={h} style={{ ...thStyle, textAlign: ['Requests', 'Blocked'].includes(h) ? 'right' : 'left' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {toolRows.map((bucket, i) => {
                            const isExpanded = expandedAppTool === bucket.key;
                            return (
                              <React.Fragment key={bucket.key}>
                                <tr
                                  onClick={() => setExpandedAppTool(isExpanded ? null : bucket.key)}
                                  style={{
                                    borderTop: '1px solid #F3EEFA',
                                    background: isExpanded ? '#F3EEFA' : (i % 2 === 0 ? 'white' : '#FAFAFA'),
                                    cursor: 'pointer',
                                  }}
                                >
                                  <td style={{ padding: '10px 16px', fontWeight: 700, color: PD }}>
                                    <span style={{ fontSize: 10, color: '#9CA3AF', marginRight: 6 }}>{isExpanded ? '▾' : '▸'}</span>
                                    {bucket.toolName}
                                    <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400, marginLeft: 6 }}>
                                      ({bucket.domainEntries.length} domain{bucket.domainEntries.length !== 1 ? 's' : ''})
                                    </span>
                                  </td>
                                  <td style={{ padding: '10px 16px', fontSize: 13, color: '#374151' }}>{bucket.vendor || '—'}</td>
                                  <td style={{ padding: '10px 16px', fontSize: 12 }}>
                                    {bucket.category
                                      ? <span style={{ background: PL, color: PD, borderRadius: 10, padding: '1px 8px', fontWeight: 600 }}>{bucket.category}</span>
                                      : '—'}
                                  </td>
                                  <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>
                                    {bucket.approved ? (
                                      <span
                                        title="Approved — click to edit"
                                        style={{ background: '#D1FAE5', color: '#065F46', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                        onClick={e => { e.stopPropagation(); openEditTool(bucket.approved); }}
                                      >
                                        ✓ Approved
                                      </span>
                                    ) : bucket.disapproved ? (
                                      <button
                                        onClick={e => { e.stopPropagation(); reApproveTool(bucket.disapproved.id, bucket.disapproved.tool_name); }}
                                        title="Click to re-approve"
                                        style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                      >
                                        ✕ Disapproved
                                      </button>
                                    ) : (
                                      <div style={{ display: 'flex', gap: 4 }}>
                                        <button
                                          onClick={e => {
                                            e.stopPropagation();
                                            const ct = bucket.catalog;
                                            let cd = ct.domains;
                                            if (typeof cd === 'string') { try { cd = JSON.parse(cd); } catch { cd = []; } }
                                            openAddTool({ registry_tool_id: ct.id, tool_name: ct.name, vendor: ct.vendor, primary_domain: cd[0] || '', domains: cd, category: ct.category });
                                          }}
                                          style={{ background: '#FEF3C7', border: '1px solid #FCD34D', color: '#78350F', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                                        >
                                          + Approve
                                        </button>
                                        <button
                                          onClick={e => { e.stopPropagation(); quickDisapproveCatalogTool(bucket.catalog); }}
                                          title="Mark as disapproved"
                                          style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, color: '#374151', textAlign: 'right' }}>{bucket.totalReqs.toLocaleString()}</td>
                                  <td style={{ padding: '10px 16px', fontSize: 13, textAlign: 'right' }}>
                                    {bucket.totalBlocked > 0
                                      ? <span style={{ color: '#991B1B', fontWeight: 600 }}>{bucket.totalBlocked.toLocaleString()}</span>
                                      : <span style={{ color: '#9CA3AF' }}>0</span>}
                                  </td>
                                </tr>
                                {isExpanded && bucket.domainEntries
                                  .sort((a, b) => b.total - a.total)
                                  .map((de, j) => (
                                  <React.Fragment key={de.root}>
                                    <tr style={{ background: '#FAF5FF', borderTop: '1px solid #EDE8F5' }}>
                                      <td style={{ padding: '6px 16px 6px 38px', fontFamily: 'monospace', fontSize: 12, color: PD }}>
                                        {de.rows.length > 1 && (
                                          <span
                                            onClick={e => { e.stopPropagation(); setExpandedAppDomain(expandedAppDomain === de.root ? null : de.root); }}
                                            style={{ fontSize: 9, color: '#9CA3AF', marginRight: 5, cursor: 'pointer' }}
                                          >
                                            {expandedAppDomain === de.root ? '▾' : '▸'}
                                          </span>
                                        )}
                                        {de.root}
                                        {de.rows.length > 1 && <span style={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'sans-serif', marginLeft: 4 }}>({de.rows.length} subdomains)</span>}
                                      </td>
                                      <td colSpan={2} style={{ padding: '6px 16px', fontSize: 11, color: '#6B7280' }}>{de.topCats}</td>
                                      <td />
                                      <td style={{ padding: '6px 16px', fontSize: 12, color: '#374151', textAlign: 'right' }}>{de.total.toLocaleString()}</td>
                                      <td style={{ padding: '6px 16px', fontSize: 12, textAlign: 'right' }}>
                                        {de.totalBlocked > 0
                                          ? <span style={{ color: '#991B1B', fontWeight: 600 }}>{de.totalBlocked.toLocaleString()}</span>
                                          : <span style={{ color: '#9CA3AF' }}>0</span>}
                                      </td>
                                    </tr>
                                    {expandedAppDomain === de.root && de.rows
                                      .filter(sub => (domainFn(sub)) !== de.root)
                                      .map((sub, k) => (
                                      <tr key={k} style={{ background: '#F5F0FF', borderTop: '1px solid #EDE8F5' }}>
                                        <td style={{ padding: '5px 16px 5px 58px', fontFamily: 'monospace', fontSize: 11, color: '#7E4DB3' }}>{domainFn(sub)}</td>
                                        <td colSpan={2} style={{ padding: '5px 16px', fontSize: 11, color: '#9CA3AF' }}>{getCats(sub)}</td>
                                        <td />
                                        <td style={{ padding: '5px 16px', fontSize: 11, color: '#6B7280', textAlign: 'right' }}>{countFn(sub).toLocaleString()}</td>
                                        <td style={{ padding: '5px 16px', fontSize: 11, textAlign: 'right', color: '#9CA3AF' }}>
                                          {(sub.requestsBlocked ?? sub.blocked ?? 0) > 0
                                            ? <span style={{ color: '#991B1B' }}>{(sub.requestsBlocked ?? sub.blocked ?? 0).toLocaleString()}</span>
                                            : '0'}
                                        </td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                ))}
                              </React.Fragment>
                            );
                          })}

                          {/* Unrecognized domains section */}
                          {unknownBuckets.length > 0 && (
                            <>
                              <tr style={{ background: '#F9FAFB', borderTop: '2px solid #E5D5F5' }}>
                                <td colSpan={6} style={{ padding: '8px 16px', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                  Unrecognized Domains — {unknownBuckets.length} · {totalUnknown.toLocaleString()} requests
                                </td>
                              </tr>
                              {unknownBuckets
                                .sort((a, b) => b.total - a.total)
                                .map((de, i) => {
                                  const isExpanded = expandedAppDomain === `unk-${de.root}`;
                                  return (
                                    <React.Fragment key={de.root}>
                                      <tr
                                        onClick={() => setExpandedAppDomain(isExpanded ? null : `unk-${de.root}`)}
                                        style={{ borderTop: '1px solid #F3EEFA', background: i % 2 === 0 ? 'white' : '#FAFAFA', cursor: 'pointer' }}
                                      >
                                        <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: 12, color: '#374151' }}>
                                          {de.rows.length > 1 && <span style={{ fontSize: 9, color: '#9CA3AF', marginRight: 5 }}>{isExpanded ? '▾' : '▸'}</span>}
                                          {de.root}
                                          {de.rows.length > 1 && <span style={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'sans-serif', marginLeft: 4 }}>({de.rows.length})</span>}
                                        </td>
                                        <td colSpan={2} style={{ padding: '8px 16px', fontSize: 12, color: '#6B7280' }}>{de.topCats}</td>
                                        <td style={{ padding: '8px 16px' }}>
                                          <button
                                            onClick={e => { e.stopPropagation(); openAddTool({ primary_domain: de.root }); }}
                                            style={{ background: 'none', border: '1px solid #E5D5F5', color: '#9CA3AF', borderRadius: 4, padding: '2px 7px', fontSize: 11, cursor: 'pointer' }}
                                          >
                                            + Approve
                                          </button>
                                        </td>
                                        <td style={{ padding: '8px 16px', fontSize: 12, textAlign: 'right', color: '#374151' }}>{de.total.toLocaleString()}</td>
                                        <td style={{ padding: '8px 16px', fontSize: 12, textAlign: 'right' }}>
                                          {de.totalBlocked > 0
                                            ? <span style={{ color: '#991B1B', fontWeight: 600 }}>{de.totalBlocked.toLocaleString()}</span>
                                            : <span style={{ color: '#9CA3AF' }}>0</span>}
                                        </td>
                                      </tr>
                                      {isExpanded && de.rows
                                        .filter(sub => domainFn(sub) !== de.root)
                                        .map((sub, k) => (
                                          <tr key={k} style={{ background: '#FAFAFA', borderTop: '1px solid #F3EEFA' }}>
                                            <td style={{ padding: '5px 16px 5px 34px', fontFamily: 'monospace', fontSize: 11, color: '#6B7280' }}>{domainFn(sub)}</td>
                                            <td colSpan={2} style={{ padding: '5px 16px', fontSize: 11, color: '#9CA3AF' }}>{getCats(sub)}</td>
                                            <td />
                                            <td style={{ padding: '5px 16px', fontSize: 11, color: '#9CA3AF', textAlign: 'right' }}>{countFn(sub).toLocaleString()}</td>
                                            <td style={{ padding: '5px 16px', fontSize: 11, textAlign: 'right', color: '#9CA3AF' }}>0</td>
                                          </tr>
                                        ))}
                                    </React.Fragment>
                                  );
                                })}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ── Users & Devices ── */}
          {umbrellaSubTab === 'users' && (
            <div>
              {umbrellaIdentities === null ? (
                <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', padding: 32, textAlign: 'center' }}>
                  {identitiesLoading
                    ? <span style={{ color: '#6B7280' }}>Loading…</span>
                    : <div>
                        <div style={{ color: '#374151', marginBottom: 12 }}>Load org-level DNS summary for this customer.</div>
                        <button onClick={loadUmbrellaIdentities} style={primaryBtn}>Load Summary</button>
                      </div>
                  }
                </div>
              ) : (() => {
                const summary      = umbrellaIdentities?.org_summary;
                const deployCounts = umbrellaIdentities?.deploy_counts || [];
                const org = summary?.organization || {};
                const orgName = org.label || org.name || decodeHtml(program?.umbrella_org_name) || program?.client_name || '—';

                // Extract numeric fields — requests-by-org nests under counts.total/totalblocked
                const requests = summary?.counts?.total    ?? summary?.requests ?? summary?.total ?? summary?.count ?? null;
                const blocked  = summary?.counts?.totalblocked ?? summary?.requestsBlocked ?? summary?.blocked ?? null;
                const sample   = umbrellaIdentities?.raw_sample || [];

                return (
                  <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5D5F5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ margin: 0, color: PD, fontSize: 15, fontWeight: 700 }}>Org Request Summary</h4>
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>DNS request totals for {orgName} from Umbrella MSP reporting</p>
                      </div>
                      <button onClick={loadUmbrellaIdentities} disabled={identitiesLoading} style={{ ...outlineBtn, fontSize: 12, padding: '4px 10px' }}>
                        {identitiesLoading ? 'Loading…' : 'Refresh'}
                      </button>
                    </div>

                    {!summary ? (
                      <div style={{ padding: '32px 40px', textAlign: 'center' }}>
                        <div style={{ fontSize: 15, color: '#374151', fontWeight: 600, marginBottom: 8 }}>Org not found in MSP data</div>
                        <div style={{ fontSize: 13, color: '#6B7280', maxWidth: 480, margin: '0 auto 16px', lineHeight: 1.6 }}>
                          This org ({program?.umbrella_org_id}) was not returned by <code>requests-by-org</code>.
                          The MSP key may not cover this org, or there were no DNS requests in the selected period.
                        </div>
                        {sample.length > 0 && (
                          <details style={{ fontSize: 11, color: '#6B7280', marginTop: 12, textAlign: 'left', maxWidth: 600, margin: '12px auto 0' }}>
                            <summary style={{ cursor: 'pointer' }}>Raw sample (first 2 orgs returned)</summary>
                            <pre style={{ background: '#F9FAFB', padding: 12, borderRadius: 6, overflow: 'auto', marginTop: 8 }}>{JSON.stringify(sample, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    ) : (
                      <div style={{ padding: 24 }}>
                        {/* Summary stat cards */}
                        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 24 }}>
                          {[
                            { label: 'Total AI Requests', value: requests !== null ? requests.toLocaleString() : '—' },
                            { label: 'Blocked',           value: blocked  !== null ? blocked.toLocaleString()  : '—', warn: blocked > 0 },
                          ].map(({ label, value, warn }) => (
                            <div key={label} style={{ background: PL, borderRadius: 8, padding: '16px 24px', minWidth: 140, textAlign: 'center' }}>
                              <div style={{ fontSize: 22, fontWeight: 700, color: warn ? '#991B1B' : PD }}>{value}</div>
                              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{label}</div>
                            </div>
                          ))}
                        </div>

                        {/* Identity type deployment breakdown */}
                        {deployCounts.length > 0 && (
                          <div style={{ marginBottom: 20 }}>
                            <h5 style={{ margin: '0 0 10px', color: PD, fontSize: 13, fontWeight: 700 }}>Deployed Identities</h5>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                              <thead>
                                <tr style={{ background: PL }}>
                                  <th style={{ ...thStyle, textAlign: 'left' }}>Type</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Active</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {deployCounts
                                  .filter(d => (d.activecount || 0) > 0 || (d.count || 0) > 0)
                                  .sort((a, b) => (b.activecount || 0) - (a.activecount || 0))
                                  .map((d, i) => {
                                    const typeName = d.identitytype?.label || d.type || '—';
                                    return (
                                      <tr key={i} style={{ borderTop: '1px solid #F3EEFA', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                                        <td style={{ padding: '9px 16px', color: '#374151' }}>{typeName}</td>
                                        <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 600, color: PD }}>{(d.activecount || 0).toLocaleString()}</td>
                                        <td style={{ padding: '9px 16px', textAlign: 'right', color: '#6B7280' }}>{(d.count || 0).toLocaleString()}</td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Per-user activity */}
                        <div style={{ marginTop: 4 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <h5 style={{ margin: 0, color: PD, fontSize: 13, fontWeight: 700 }}>Activity by User / Device</h5>
                            <button
                              onClick={loadUmbrellaUserActivity}
                              disabled={userActivityLoading}
                              style={{ ...outlineBtn, fontSize: 12, padding: '4px 10px' }}
                            >
                              {userActivityLoading ? 'Loading…' : 'Refresh'}
                            </button>
                          </div>

                          {umbrellaUserActivity === null ? (
                            <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                              {userActivityLoading ? 'Loading…' : 'Loading user activity…'}
                            </div>
                          ) : umbrellaUserActivity.identities?.length === 0 ? (
                            <div style={{ fontSize: 13, color: '#6B7280', padding: '12px 0' }}>
                              No activity found for the selected date range.
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>
                                {umbrellaUserActivity.identities.length} identities ·{' '}
                                {umbrellaUserActivity.count_method === 'aggregate'
                                  ? <>{umbrellaUserActivity.total_events?.toLocaleString()} total requests (aggregate) · click a row to expand</>
                                  : <>{umbrellaUserActivity.total_events?.toLocaleString()} events sampled (log-based — may not match total) · click a row to expand</>
                                }
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                  <tr style={{ background: PL }}>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>User / Device</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>Type</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Requests</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Blocked</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {umbrellaUserActivity.identities.map((ident, i) => (
                                    <React.Fragment key={i}>
                                      <tr
                                        onClick={() => setExpandedUser(expandedUser === i ? null : i)}
                                        style={{
                                          borderTop: '1px solid #F3EEFA',
                                          background: expandedUser === i ? '#F3EEFA' : (i % 2 === 0 ? 'white' : '#FAFAFA'),
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <td style={{ padding: '9px 16px', color: PD, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                          <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>{expandedUser === i ? '▾' : '▸'}</span>
                                          {ident.label}
                                        </td>
                                        <td style={{ padding: '9px 16px', color: '#6B7280' }}>{ident.type}</td>
                                        <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>{ident.requests.toLocaleString()}</td>
                                        <td style={{ padding: '9px 16px', textAlign: 'right', color: ident.blocked > 0 ? '#991B1B' : '#9CA3AF', fontWeight: ident.blocked > 0 ? 700 : 400 }}>
                                          {ident.blocked > 0 ? ident.blocked.toLocaleString() : '—'}
                                        </td>
                                      </tr>
                                      {expandedUser === i && (
                                        <tr style={{ background: '#FAF5FF' }}>
                                          <td colSpan={4} style={{ padding: '8px 32px 16px' }}>
                                            <div style={{ fontSize: 12, color: PD, fontWeight: 600, marginBottom: 6 }}>
                                              AI Tools used by {ident.label}
                                            </div>
                                            {(() => {
                                              // Build tool lookup maps (same logic as Apps tab)
                                              const uCatalogById   = {};
                                              const uCatalogByName = {};
                                              const uCatalogByRoot = {};
                                              toolRegistry.forEach(ct => {
                                                uCatalogById[ct.id]                          = ct;
                                                uCatalogByName[(ct.name || '').toLowerCase()] = ct;
                                                let ctd = ct.domains;
                                                if (typeof ctd === 'string') { try { ctd = JSON.parse(ctd); } catch { ctd = []; } }
                                                (Array.isArray(ctd) ? ctd : []).forEach(d => { uCatalogByRoot[getRootDomain(d)] = ct; });
                                              });
                                              const uApprovedRootMap = {};
                                              approvedTools.forEach(t => {
                                                let doms = t.domains;
                                                if (typeof doms === 'string') { try { doms = JSON.parse(doms); } catch { doms = []; } }
                                                if (!Array.isArray(doms)) doms = [];
                                                if (t.primary_domain && !doms.includes(t.primary_domain)) doms = [t.primary_domain, ...doms];
                                                const regTool = t.registry_tool_id
                                                  ? uCatalogById[t.registry_tool_id]
                                                  : uCatalogByName[(t.tool_name || '').toLowerCase()];
                                                if (regTool) {
                                                  let rd = regTool.domains;
                                                  if (typeof rd === 'string') { try { rd = JSON.parse(rd); } catch { rd = []; } }
                                                  if (Array.isArray(rd)) doms = [...new Set([...doms, ...rd])];
                                                }
                                                doms.forEach(d => { uApprovedRootMap[getRootDomain(d)] = t; });
                                              });

                                              // Group the user's top_domains by tool
                                              const toolBuckets = {};
                                              const unknownDoms  = [];
                                              (ident.top_domains || []).forEach(item => {
                                                const root     = getRootDomain(item.domain);
                                                const approved = uApprovedRootMap[root];
                                                const catalog  = !approved && uCatalogByRoot[root];
                                                const toolKey  = approved ? `a-${approved.id}` : catalog ? `c-${catalog.id}` : null;
                                                if (toolKey) {
                                                  if (!toolBuckets[toolKey]) {
                                                    toolBuckets[toolKey] = {
                                                      toolName: approved ? approved.tool_name : catalog.name,
                                                      approved: !!approved,
                                                      total: 0,
                                                      domains: [],
                                                    };
                                                  }
                                                  toolBuckets[toolKey].total += item.count;
                                                  toolBuckets[toolKey].domains.push(item);
                                                } else {
                                                  // Merge into existing unknown entry for same root
                                                  const ex = unknownDoms.find(u => u.root === root);
                                                  if (ex) { ex.total += item.count; ex.domains.push(item); }
                                                  else     unknownDoms.push({ root, total: item.count, domains: [item] });
                                                }
                                              });

                                              const toolRows = Object.values(toolBuckets).sort((a, b) => b.total - a.total);
                                              unknownDoms.sort((a, b) => b.total - a.total);

                                              return (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                                  <thead>
                                                    <tr>
                                                      <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6B7280', fontWeight: 600, borderBottom: '1px solid #E5D5F5' }}>Tool</th>
                                                      <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6B7280', fontWeight: 600, borderBottom: '1px solid #E5D5F5' }}>Status</th>
                                                      <th style={{ textAlign: 'right', padding: '4px 8px', color: '#6B7280', fontWeight: 600, borderBottom: '1px solid #E5D5F5' }}>Requests</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {toolRows.map((tb, j) => {
                                                      const udKey     = `${i}-tool-${j}`;
                                                      const udExpanded = expandedUserDomain[udKey];
                                                      const hasDoms   = tb.domains.length > 1 || (tb.domains.length === 1 && tb.domains[0].domain !== tb.toolName);
                                                      return (
                                                        <React.Fragment key={j}>
                                                          <tr
                                                            onClick={() => hasDoms && setExpandedUserDomain(prev => ({ ...prev, [udKey]: !prev[udKey] }))}
                                                            style={{ borderTop: j > 0 ? '1px solid #F3EEFA' : 'none', cursor: hasDoms ? 'pointer' : 'default', background: udExpanded ? '#EDE8F5' : 'transparent' }}
                                                          >
                                                            <td style={{ padding: '6px 8px', color: PD, fontWeight: 700 }}>
                                                              {hasDoms && <span style={{ fontSize: 9, color: '#9CA3AF', marginRight: 4 }}>{udExpanded ? '▾' : '▸'}</span>}
                                                              {tb.toolName}
                                                            </td>
                                                            <td style={{ padding: '6px 8px' }}>
                                                              {tb.approved
                                                                ? <span style={{ background: '#D1FAE5', color: '#065F46', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>✓ Approved</span>
                                                                : <span style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>Not Approved</span>
                                                              }
                                                            </td>
                                                            <td style={{ padding: '6px 8px', textAlign: 'right', color: PD, fontWeight: 700 }}>{tb.total.toLocaleString()}</td>
                                                          </tr>
                                                          {udExpanded && tb.domains.sort((a, b) => b.count - a.count).map((sub, k) => (
                                                            <tr key={k} style={{ background: '#FAF5FF', borderTop: '1px solid #EDE8F5' }}>
                                                              <td style={{ padding: '3px 8px 3px 24px', color: '#6B3FA0', fontFamily: 'monospace', fontSize: 11 }} colSpan={2}>{sub.domain}</td>
                                                              <td style={{ padding: '3px 8px', textAlign: 'right', color: '#6B7280', fontSize: 11 }}>{sub.count.toLocaleString()}</td>
                                                            </tr>
                                                          ))}
                                                        </React.Fragment>
                                                      );
                                                    })}
                                                    {unknownDoms.map((ud, j) => {
                                                      const udKey     = `${i}-unk-${j}`;
                                                      const udExpanded = expandedUserDomain[udKey];
                                                      const hasDoms   = ud.domains.length > 1;
                                                      return (
                                                        <React.Fragment key={`unk-${j}`}>
                                                          <tr
                                                            onClick={() => hasDoms && setExpandedUserDomain(prev => ({ ...prev, [udKey]: !prev[udKey] }))}
                                                            style={{ borderTop: '1px solid #F3EEFA', cursor: hasDoms ? 'pointer' : 'default', opacity: 0.7 }}
                                                          >
                                                            <td style={{ padding: '6px 8px', color: '#374151', fontFamily: 'monospace' }}>
                                                              {hasDoms && <span style={{ fontSize: 9, color: '#9CA3AF', marginRight: 4 }}>{udExpanded ? '▾' : '▸'}</span>}
                                                              {ud.root}
                                                            </td>
                                                            <td style={{ padding: '6px 8px' }}>
                                                              <span style={{ background: '#F3F4F6', color: '#9CA3AF', borderRadius: 4, padding: '1px 6px', fontSize: 10 }}>Unrecognized</span>
                                                            </td>
                                                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#6B7280' }}>{ud.total.toLocaleString()}</td>
                                                          </tr>
                                                          {udExpanded && ud.domains.sort((a, b) => b.count - a.count).map((sub, k) => (
                                                            <tr key={k} style={{ background: '#FAFAFA', borderTop: '1px solid #F3EEFA' }}>
                                                              <td style={{ padding: '3px 8px 3px 24px', color: '#9CA3AF', fontFamily: 'monospace', fontSize: 11 }} colSpan={2}>{sub.domain}</td>
                                                              <td style={{ padding: '3px 8px', textAlign: 'right', color: '#9CA3AF', fontSize: 11 }}>{sub.count.toLocaleString()}</td>
                                                            </tr>
                                                          ))}
                                                        </React.Fragment>
                                                      );
                                                    })}
                                                  </tbody>
                                                </table>
                                              );
                                            })()}
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

        </div>
      )}

      {/* ── Edit modal ── */}
      {showEdit && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: PD, fontSize: 18, fontWeight: 700 }}>Edit Program</h3>
              <button onClick={() => setShowEdit(false)} style={closeBtn}>✕</button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Tier</label>
                  <select value={editForm.tier_id} onChange={e => setEditForm(f => ({ ...f, tier_id: e.target.value }))} style={inputStyle}>
                    {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Status</label>
                  <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                    {['active','paused','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Next Assessment Type</label>
                  <select value={editForm.next_assessment_type} onChange={e => setEditForm(f => ({ ...f, next_assessment_type: e.target.value }))} style={inputStyle}>
                    <option value="full">Full (72 questions)</option>
                    <option value="core">Core (27 questions)</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Umbrella Org ID</label>
                  <input type="text" value={editForm.umbrella_org_id} onChange={e => setEditForm(f => ({ ...f, umbrella_org_id: e.target.value }))} style={inputStyle} placeholder="e.g. 12345678" />
                </div>
                <div>
                  <label style={labelStyle}>Umbrella Org Name</label>
                  <input type="text" value={editForm.umbrella_org_name} onChange={e => setEditForm(f => ({ ...f, umbrella_org_name: e.target.value }))} style={inputStyle} placeholder="e.g. Acme Corp" />
                </div>
                <div>
                  <label style={labelStyle}>Notes</label>
                  <textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" onClick={() => setShowEdit(false)} style={outlineBtn} disabled={saving}>Cancel</button>
                <button type="submit" style={primaryBtn} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Complete task modal ── */}
      {completingTask && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: 400 }}>
            <h3 style={{ margin: '0 0 16px', color: PD, fontSize: 18, fontWeight: 700 }}>Complete Task</h3>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea
              value={taskNotes}
              onChange={e => setTaskNotes(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', marginBottom: 16 }}
              placeholder="Add completion notes…"
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setCompletingTask(null); setTaskNotes(''); }} style={outlineBtn}>Cancel</button>
              <button onClick={() => handleCompleteTask(completingTask)} style={primaryBtn}>Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Schedule Task Dialog ── */}
      {schedulingTask && (
        <AiGovScheduleDialog
          programId={programId}
          task={schedulingTask}
          clientName={program.client_name}
          onClose={() => setSchedulingTask(null)}
          onSaved={() => { load(); setSchedulingTask(null); }}
        />
      )}

      {/* ── Approved Tool Modal ── */}
      {toolModalOpen && (() => {
        const selectedReg = toolRegistry.find(r => r.id === toolForm.registry_tool_id) || null;
        const filteredReg = toolRegistry.filter(r =>
          !registrySearch || r.name.toLowerCase().includes(registrySearch.toLowerCase()) ||
          (r.vendor || '').toLowerCase().includes(registrySearch.toLowerCase())
        );
        const selectRegistryTool = (rt) => {
          let doms = rt.domains;
          if (typeof doms === 'string') { try { doms = JSON.parse(doms); } catch { doms = []; } }
          if (!Array.isArray(doms)) doms = [];
          setToolForm(p => ({
            ...p,
            registry_tool_id: rt.id,
            tool_name: rt.name,
            vendor: rt.vendor || '',
            category: rt.category || '',
            domains: doms,
          }));
          setRegistrySearch(rt.name);
          setToolCustomMode(false);
        };
        const formDomains = Array.isArray(toolForm.domains) ? toolForm.domains : [];
        const domainInput = toolForm._domainInput || '';
        const setDomainInput = v => setToolForm(p => ({ ...p, _domainInput: v }));
        const addDomain = (val) => {
          const d = (val || domainInput).trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0];
          if (d && !formDomains.includes(d)) setToolForm(p => ({ ...p, domains: [...formDomains, d], _domainInput: '' }));
          else setToolForm(p => ({ ...p, _domainInput: '' }));
        };
        const removeDomain = d => setToolForm(p => ({ ...p, domains: formDomains.filter(x => x !== d) }));
        // All domains known to the registry (for the custom-tool domain suggestions)
        const allRegistryDomains = Array.from(new Set(
          toolRegistry.flatMap(r => { let d = r.domains; if (typeof d === 'string') { try { d = JSON.parse(d); } catch { d = []; } } return Array.isArray(d) ? d : []; })
        )).sort();
        const showDropdown = registrySearch.length > 0 && !selectedReg && filteredReg.length > 0;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'white', borderRadius: 10, padding: 28, width: 580, maxWidth: '95vw', boxShadow: '0 24px 80px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, color: PD }}>{editingToolId ? 'Edit Approved Tool' : 'Approve a Tool'}</h3>
              <p style={{ margin: '0 0 18px', fontSize: 12, color: '#6B7280' }}>Select from the global registry — domains and vendor are shared across all clients.</p>

              {/* Registry picker */}
              {!toolCustomMode && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 3 }}>Tool *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      value={selectedReg ? selectedReg.name : registrySearch}
                      placeholder="Search registry (e.g. ChatGPT, Copilot…)"
                      onChange={e => { setRegistrySearch(e.target.value); setToolForm(p => ({ ...p, registry_tool_id: null })); }}
                      style={{ width: '100%', padding: '8px 10px', border: `1px solid ${selectedReg ? '#6D28D9' : '#D1D5DB'}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: selectedReg ? '#F5F3FF' : 'white' }}
                    />
                    {selectedReg && (
                      <button onClick={() => { setToolForm(p => ({ ...p, registry_tool_id: null, domains: [] })); setRegistrySearch(''); }}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 16 }}>×</button>
                    )}
                    {showDropdown && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #D1D5DB', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 10, maxHeight: 220, overflowY: 'auto' }}>
                        {filteredReg.map(rt => {
                          let d = rt.domains; if (typeof d === 'string') { try { d = JSON.parse(d); } catch { d = []; } }
                          return (
                            <div key={rt.id} onClick={() => selectRegistryTool(rt)}
                              style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#F5F3FF'}
                              onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                              <div>
                                <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{rt.name}</span>
                                <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 6 }}>{rt.vendor}</span>
                              </div>
                              <span style={{ fontSize: 11, color: '#6B7280', fontFamily: 'monospace' }}>{Array.isArray(d) ? d[0] : ''}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {selectedReg && (() => {
                    let d = selectedReg.domains; if (typeof d === 'string') { try { d = JSON.parse(d); } catch { d = []; } }
                    return (
                      <div style={{ marginTop: 8, padding: '8px 12px', background: '#F5F3FF', borderRadius: 6, border: '1px solid #DDD6FE' }}>
                        <div style={{ fontSize: 12, color: '#5B21B6', marginBottom: 4 }}>
                          <strong>{selectedReg.vendor}</strong> · {selectedReg.category}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {(Array.isArray(d) ? d : []).map(dom => (
                            <span key={dom} style={{ background: '#EDE9FE', color: '#5B21B6', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontFamily: 'monospace' }}>{dom}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{ marginTop: 8, fontSize: 12, color: '#9CA3AF' }}>
                    Can't find it?{' '}
                    <button onClick={() => { setToolCustomMode(true); setToolForm(p => ({ ...p, registry_tool_id: null })); }}
                      style={{ background: 'none', border: 'none', color: PD, cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline' }}>
                      Add as custom tool
                    </button>
                  </div>
                </div>
              )}

              {/* Custom tool fields */}
              {toolCustomMode && (
                <div style={{ marginBottom: 16, padding: 12, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#92400E' }}>Custom tool (not in registry)</span>
                    <button onClick={() => setToolCustomMode(false)} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 12 }}>← Back to registry</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
                    {[{ key: 'tool_name', label: 'Tool Name *', span: 1 }, { key: 'vendor', label: 'Vendor', span: 1 }, { key: 'category', label: 'Category', span: 2 }].map(f => (
                      <div key={f.key} style={{ gridColumn: f.span === 2 ? '1 / -1' : undefined }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 3 }}>{f.label}</label>
                        <input value={toolForm[f.key] || ''} onChange={e => setToolForm(p => ({ ...p, [f.key]: e.target.value }))}
                          style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                      </div>
                    ))}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 3 }}>Domains</label>
                      <div style={{ border: '1px solid #D1D5DB', borderRadius: 6, padding: '6px 8px', minHeight: 36, display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 5 }}>
                        {formDomains.map(d => (
                          <span key={d} style={{ background: '#EDE9FE', color: '#5B21B6', borderRadius: 4, padding: '2px 6px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 3 }}>
                            {d} <button onClick={() => removeDomain(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7C3AED', fontWeight: 700, padding: 0 }}>×</button>
                          </span>
                        ))}
                        {formDomains.length === 0 && <span style={{ color: '#D1D5DB', fontSize: 12 }}>e.g. example.com</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <input value={domainInput} placeholder="example.com" list="custom-tool-domains"
                          onChange={e => setDomainInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
                          style={{ flex: 1, padding: '6px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 12 }} />
                        <button onClick={() => addDomain()} style={{ ...outlineBtn, fontSize: 12, padding: '5px 10px' }}>Add</button>
                      </div>
                      <datalist id="custom-tool-domains">
                        {allRegistryDomains.map(d => <option key={d} value={d} />)}
                      </datalist>
                    </div>
                  </div>
                </div>
              )}

              {/* Use + Restrictions — always shown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 3 }}>Approved Use <span style={{ fontWeight: 400 }}>— how this client is permitted to use the tool</span></label>
                  <textarea rows={2} value={toolForm.use_description || ''} onChange={e => setToolForm(p => ({ ...p, use_description: e.target.value }))}
                    placeholder="e.g. Business writing, summarization, internal Q&A only"
                    style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 3 }}>Restrictions</label>
                  <textarea rows={2} value={toolForm.restrictions || ''} onChange={e => setToolForm(p => ({ ...p, restrictions: e.target.value }))}
                    placeholder="e.g. No confidential client data, no PHI, no code from proprietary systems"
                    style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 3 }}>Subscription Tier <span style={{ fontWeight: 400, color: '#9CA3AF' }}>— manually set; cannot be detected from DNS</span></label>
                  <select value={toolForm.tier || ''} onChange={e => setToolForm(p => ({ ...p, tier: e.target.value || null }))}
                    style={{ width: '100%', padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}>
                    <option value=''>Unknown / Not set</option>
                    <option value='Free'>Free</option>
                    <option value='Paid'>Paid</option>
                    <option value='Enterprise'>Enterprise</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button onClick={() => { setToolModalOpen(false); setToolForm({}); setEditingToolId(null); setRegistrySearch(''); setToolCustomMode(false); }} style={{ ...outlineBtn, fontSize: 13, padding: '7px 16px' }}>Cancel</button>
                <button onClick={saveTool} disabled={savingTool || (!toolForm.registry_tool_id && !toolForm.tool_name)} style={{ ...primaryBtn, fontSize: 13, padding: '7px 16px' }}>{savingTool ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function Card({ title, children }) {
  return (
    <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', padding: 20 }}>
      <h4 style={{ margin: '0 0 14px', color: '#4A2B70', fontSize: 15, fontWeight: 700 }}>{title}</h4>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F3EEFA', paddingBottom: 8, marginBottom: 8, fontSize: 14 }}>
      <span style={{ color: '#6B7280', fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#374151', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{children}</span>
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────
const primaryBtn = {
  padding: '8px 18px', backgroundColor: '#6B3FA0', color: 'white',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const outlineBtn = {
  padding: '8px 18px', backgroundColor: 'transparent', color: '#6B3FA0',
  border: '2px solid #6B3FA0', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(74,43,112,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalBox = {
  background: 'white', borderRadius: 12, padding: 28,
  boxShadow: '0 20px 60px rgba(74,43,112,0.2)', maxHeight: '90vh', overflowY: 'auto',
};
const closeBtn = {
  background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF',
};
const labelStyle = { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#4A2B70' };
const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #E5D5F5', borderRadius: 6,
  fontSize: 14, color: '#374151', boxSizing: 'border-box', outline: 'none',
};
const thStyle = {
  padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700,
  color: '#4A2B70', textTransform: 'uppercase', letterSpacing: '0.05em',
};
